<template>
    <div class="object-card-page">
        <header class="object-card-header" :class="!isCreate && card ? `object-card-header--${status}` : ''">
            <div class="object-card-header__toolbar">
                <div>
                    <b-button size="sm" variant="outline-secondary" @click="goBack">
                        <i class="fas fa-arrow-left mr-1"></i>
                        Назад
                    </b-button>
                </div>
                <div class="object-card-title">
                    <h1>{{ title }}</h1>
                    <b-badge v-if="!isCreate && card" :variant="statusVariant(status)">
                        {{ statusLabel }}
                    </b-badge>
                    <b-button
                        v-if="!editMode && card && canEditObject"
                        v-b-tooltip.hover.focus
                        size="sm"
                        variant="outline-secondary"
                        class="object-section-edit"
                        title="Редактировать основные данные"
                        aria-label="Редактировать основные данные"
                        :disabled="sectionSaving || cardLoading"
                        @click="openObjectEditModal"
                    ><i class="fas fa-pen" aria-hidden="true"></i></b-button>
                </div>
                <div class="object-card-actions">
                    <b-button
                        v-if="!isCreate && buildingProcessId"
                        variant="outline-success"
                        size="sm"
                        :disabled="reportDownloading || cardLoading || deleting"
                        @click="downloadObjectReport"
                    >
                        <b-spinner v-if="reportDownloading" small class="mr-1"></b-spinner>
                        <i v-else class="fas fa-file-excel mr-1"></i>
                        Скачать XLS
                    </b-button>
                    <b-dropdown
                        v-if="!editMode && canDeleteObject"
                        variant="outline-secondary"
                        size="sm"
                        right
                        no-caret
                        toggle-text="Действия с объектом"
                        :disabled="cardLoading || deleting"
                    >
                        <template #button-content><i class="fas fa-ellipsis-h" aria-hidden="true"></i><span class="sr-only">Действия с объектом</span></template>
                        <b-dropdown-item-button variant="danger" @click="confirmDeleteObject">
                            <i class="fas fa-trash-alt mr-1" aria-hidden="true"></i> Удалить объект
                        </b-dropdown-item-button>
                    </b-dropdown>
                    <b-button
                        v-if="editMode && canSave"
                        variant="success"
                        size="sm"
                        :disabled="saving || cardLoading || deleting"
                        @click="save"
                    >
                        <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                        <i v-else class="fas fa-save mr-1"></i>
                        Сохранить
                    </b-button>
                    <b-button
                        v-if="editMode && !isCreate"
                        variant="outline-secondary"
                        size="sm"
                        :disabled="saving || deleting"
                        @click="cancelEdit"
                    >
                        Отмена
                    </b-button>
                </div>
            </div>
            <ObjectTelegramSummary v-if="!isCreate && card && !cardLoading" :card="card" :editing="editMode" :error="telegramSummaryError" @membership="$refs.groupMembership.open(card)" @manage="openTelegramManagement" @refresh="refreshTelegramState"/>
            <div v-if="!editMode && card && !cardLoading" class="object-card-summary">
                <div class="object-card-context">
                    <div v-if="form.address" class="object-card-address">
                        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                        <span>{{ form.address }}</span>
                    </div>
                    <details :key="card.building_id" class="object-card-details">
                        <summary>Сведения</summary>
                        <dl>
                            <div v-for="detail in objectDetails" :key="detail.key">
                                <dt>{{ detail.label }}</dt>
                                <dd>{{ detail.value }}</dd>
                            </div>
                        </dl>
                    </details>
                    <router-link v-if="card.source_preparation_id" :to="`/objects/preparations/${card.source_preparation_id}?history=1`" class="small">История подготовки</router-link>
                </div>
                <p v-if="card && card.is_history_only" class="small text-muted mt-2 mb-0">Объект сохранён для исторических табелей</p>
                <div v-if="objectHealthItems.length || objectLastReportBadge" class="object-health-row">
                    <component
                        :is="objectLastReportPath ? 'router-link' : 'span'"
                        v-if="objectLastReportBadge"
                        v-b-tooltip.hover.focus
                        :to="objectLastReportPath || undefined"
                        :tabindex="objectLastReportPath ? null : 0"
                        class="object-last-report"
                        :title="objectLastReportBadge.title"
                    >
                        <i class="fas fa-clipboard-list" aria-hidden="true"></i>
                        Последний отчёт: {{ objectLastReportBadge.text }}
                    </component>
                    <button
                        v-for="item in objectHealthItems"
                        :key="item.key"
                        v-b-tooltip.hover.focus
                        type="button"
                        class="object-health-chip"
                        :class="`object-health-chip--${item.variant}`"
                        :title="item.title || item.text"
                        @click="openHealthItem(item.key)"
                    >
                        <i :class="item.icon" aria-hidden="true"></i>
                        {{ item.text }}
                    </button>
                </div>
            </div>
        </header>
        <ObjectGroupMembership ref="groupMembership" @changed="refreshTelegramState"/>

        <b-alert
            v-if="errorMessage"
            variant="danger"
            show
            dismissible
            class="mb-3"
            @dismissed="errorMessage = null"
        >
            {{ errorMessage }}
        </b-alert>

        <div v-if="cardLoading && !isCreate" class="text-center text-muted py-4">
            <b-spinner small class="mr-1"></b-spinner>
            Загрузка карточки
        </div>

        <div v-else class="object-card-content">
            <div v-if="!editMode" class="object-overview">
                <div class="object-overview-grid">
                    <section ref="teamPanel" tabindex="-1" class="object-panel object-team-panel object-summary-target">
                        <div class="object-panel__header object-panel__header--split">
                            <h6>Команда объекта</h6>
                            <b-button
                                v-if="canEditProcess"
                                size="sm"
                                variant="outline-secondary"
                                title="Редактировать команду и доступ"
                                :disabled="sectionSaving"
                                @click="openTeamEditModal"
                            >
                                <i class="fas fa-pen"></i>
                            </b-button>
                        </div>
                        <div class="object-team-grid">
                            <div
                                v-for="member in primaryTeamMembers"
                                :key="member.key"
                                class="object-person-card"
                            >
                                <div class="object-person-card__body">
                                    <small>{{ member.role }}</small>
                                    <EmployeeLink
                                        v-if="member.user"
                                        :user="member.user"
                                        name-order="last-first"
                                        class="object-person-card__name"
                                    />
                                    <div v-else class="object-person-card__name">{{ member.name }}</div>
                                </div>
                            </div>
                        </div>
                        <div v-if="selectedHasAccessUsers.length" class="object-access-list">
                            <div class="object-access-list__title">Имеющие доступ</div>
                            <div class="object-access-list__chips">
                                <EmployeeLink
                                    v-for="user in selectedHasAccessUsersWithTelegram"
                                    :key="user.user_id"
                                    :user="user"
                                    name-order="last-first"
                                    class="object-access-chip"
                                />
                                <EmployeeLink
                                    v-for="user in selectedHasAccessUsersWithoutTelegram"
                                    :key="user.user_id"
                                    :user="user"
                                    name-order="last-first"
                                    class="object-access-chip"
                                />
                            </div>
                        </div>
                        <ObjectTelegramGroups
                            ref="telegramGroups"
                            v-if="card"
                            :key="card.building_id"
                            :card="card"
                            @main-linked="load"
                            @changed="updateTelegramState"
                            @state-error="telegramSummaryError = $event"
                        />
                    </section>

                    <section class="object-panel">
                        <div class="object-panel__header object-panel__header--split">
                            <h6>Сводка</h6>
                            <b-button
                                v-if="canEditProcess"
                                size="sm"
                                variant="outline-secondary"
                                title="Редактировать договор и связи"
                                :disabled="sectionSaving"
                                @click="openContractEditModal"
                            >
                                <i class="fas fa-pen"></i>
                            </b-button>
                        </div>
                        <div class="object-fact-grid">
                            <div
                                v-for="fact in objectFacts"
                                :key="fact.key"
                                class="object-fact"
                                :class="{ 'object-fact--wide': fact.multiline }"
                            >
                                <span class="object-fact__label">
                                    <i :class="fact.icon"></i>
                                    {{ fact.label }}
                                </span>
                                <a
                                    v-if="fact.href"
                                    :href="fact.href"
                                    target="_blank"
                                    class="object-fact__value"
                                    :class="{ 'object-fact__value--multiline': fact.multiline }"
                                >
                                    {{ fact.value }}
                                </a>
                                <button
                                    v-else-if="fact.action"
                                    type="button"
                                    class="object-fact__value object-fact__value--button"
                                    :class="{ 'object-fact__value--multiline': fact.multiline }"
                                    :title="fact.title"
                                    @click="handleFactAction(fact)"
                                >
                                    {{ fact.value }}
                                </button>
                                <strong
                                    v-else
                                    class="object-fact__value"
                                    :class="{ 'object-fact__value--multiline': fact.multiline }"
                                >
                                    {{ fact.value }}
                                </strong>
                            </div>
                        </div>
                    </section>

                    <section ref="processPanel" tabindex="-1" class="object-panel object-process-panel object-summary-target">
                        <div class="object-panel__header object-panel__header--split">
                            <h6>Рабочий процесс</h6>
                            <div class="object-panel__header-actions">
                                <b-badge v-if="hasProcess || processEnabled" :variant="statusVariant(status)">
                                    {{ statusLabel }}
                                </b-badge>
                                <b-button
                                    v-if="canEditProcess || (!hasProcess && canCreateProcess)"
                                    size="sm"
                                    variant="outline-secondary"
                                    title="Редактировать рабочий процесс"
                                    :disabled="sectionSaving"
                                    @click="openProcessEditModal"
                                >
                                    <i class="fas fa-pen"></i>
                                </b-button>
                            </div>
                        </div>
                        <div v-if="hasProcess || processEnabled" class="object-process-summary">
                            <div
                                v-for="item in processFacts"
                                :key="item.key"
                                class="object-process-item"
                            >
                                <span>
                                    <i :class="item.icon"></i>
                                    {{ item.label }}
                                </span>
                                <strong>{{ item.value }}</strong>
                            </div>
                        </div>
                        <div v-else class="object-empty-state">
                            <i class="fas fa-layer-group"></i>
                            <span>Рабочий процесс не создан</span>
                        </div>
                    </section>
                </div>

                <section v-if="hasProcess" ref="progressPanel" tabindex="-1" class="object-panel object-progress-panel object-summary-target">
                    <div class="object-panel__header object-panel__header--split">
                        <div>
                            <h6>Прогресс работ</h6>
                            <small v-if="workProgressHeaderText" class="text-muted">
                                {{ workProgressHeaderText }}
                            </small>
                        </div>
                        <div class="object-progress-actions">
                            <b-button
                                v-if="canCreateTarget"
                                size="sm"
                                variant="outline-primary"
                                :disabled="targetSaving || progressLoading"
                                title="Добавить работу"
                                @click="openCreateTargetModal"
                            >
                                <i class="fas fa-plus"></i>
                                <span>Работа</span>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                :title="progressCollapsed ? 'Развернуть прогресс' : 'Свернуть прогресс'"
                                @click="progressCollapsed = !progressCollapsed"
                            >
                                <i :class="progressCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                :disabled="progressLoading"
                                title="Обновить прогресс"
                                @click="refreshWorkProgress"
                            >
                                <b-spinner v-if="progressLoading" small></b-spinner>
                                <i v-else class="fas fa-sync-alt"></i>
                            </b-button>
                        </div>
                    </div>

                    <b-collapse :visible="!progressCollapsed">
                        <div v-if="progressLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка прогресса
                        </div>
                        <b-alert v-else-if="workProgressError" show variant="danger">
                            {{ workProgressError }}
                        </b-alert>
                        <div v-else-if="!workProgressWorks.length" class="object-empty-state">
                            <i class="fas fa-chart-line"></i>
                            <span>План работ по объекту не заполнен</span>
                        </div>
                        <div v-else>
                            <div class="object-progress-tabs">
                                <button
                                    type="button"
                                    class="object-progress-tab"
                                    :class="{ 'object-progress-tab--active': workProgressView === 'works' }"
                                    @click="setWorkProgressView('works')"
                                >
                                    Работы
                                </button>
                                <button
                                    type="button"
                                    class="object-progress-tab"
                                    :class="{ 'object-progress-tab--active': workProgressView === 'groups' }"
                                    @click="setWorkProgressView('groups')"
                                >
                                    Группы работ
                                </button>
                            </div>

                            <div class="object-progress-controls">
                                <div class="object-progress-filters" @wheel="scrollProgressFiltersOnWheel">
                                    <button
                                        v-for="filter in workProgressFilterOptions"
                                        :key="filter.key"
                                        type="button"
                                        class="object-progress-filter"
                                        :class="[
                                            `object-progress-filter--${filter.variant}`,
                                            { 'object-progress-filter--active': workProgressFilter === filter.key }
                                        ]"
                                        @click="setWorkProgressFilter(filter.key)"
                                    >
                                        <i
                                            :class="workProgressFilter === filter.key ? 'fas fa-check' : filter.icon"
                                            aria-hidden="true"
                                        ></i>
                                        <span>{{ filter.text }}</span>
                                        <strong>{{ filter.count }}</strong>
                                    </button>
                                </div>
                                <div class="object-progress-search-wrap" :class="{ 'object-progress-search-wrap--active': workProgressSearch }">
                                    <i class="fas fa-search" aria-hidden="true"></i>
                                    <b-form-input
                                        v-model.trim="workProgressSearch"
                                        type="search"
                                        size="sm"
                                        :placeholder="workProgressSearchPlaceholder"
                                        class="object-progress-search"
                                    ></b-form-input>
                                </div>
                            </div>

                            <div
                                v-if="workProgressView === 'works' && !filteredWorkProgressWorks.length"
                                class="object-empty-state"
                            >
                                <i class="fas fa-search"></i>
                                <span>По выбранным фильтрам ничего не найдено</span>
                            </div>
                            <div
                                v-else-if="workProgressView === 'groups' && !filteredWorkProgressGroups.length"
                                class="object-empty-state"
                            >
                                <i class="fas fa-layer-group"></i>
                                <span>По выбранным фильтрам группы не найдены</span>
                            </div>

                            <div
                                v-else-if="workProgressView === 'works'"
                                class="object-work-progress-grid"
                            >
                                <article
                                    v-for="work in filteredWorkProgressWorks"
                                    :key="work.id || work.work_id"
                                    class="object-work-progress-card"
                                    :class="[
                                        `object-work-progress-card--${work.status}`,
                                        {'object-work-progress-card--disabled': work.is_available === false}
                                    ]"
                                >
                                    <div class="object-work-progress-card__header">
                                        <div
                                            class="object-work-progress-card__title"
                                            :title="workHeaderTitle(work)"
                                        >
                                            <strong>{{ work.name }}</strong>
                                            <span v-if="progressDisplayText(work.project_name)">
                                                {{ progressDisplayText(work.project_name) }}
                                            </span>
                                            <span
                                                v-if="workGroupPath(work)"
                                                class="object-work-progress-card__group"
                                            >
                                                {{ workGroupPath(work) }}
                                            </span>
                                        </div>
                                        <div class="object-work-progress-card__side">
                                            <b-badge :variant="workProgressVariant(work)">
                                                {{ work.status_label }}
                                            </b-badge>
                                            <b-badge v-if="work.is_active === false" variant="secondary">
                                                Отключена
                                            </b-badge>
                                            <b-badge v-else-if="work.directory_is_active === false" variant="secondary">
                                                Вид работ отключён в справочнике
                                            </b-badge>
                                            <div
                                                v-if="work.work_id && (canCreateMaterialTarget || canUpdateTarget || canDeleteTarget)"
                                                class="object-work-progress-card__actions"
                                            >
                                                <b-button
                                                    v-if="canCreateMaterialTarget && work.id && work.is_available !== false"
                                                    size="sm"
                                                    variant="outline-success"
                                                    title="Добавить материал"
                                                    @click="openCreateMaterialTargetModal(work)"
                                                >
                                                    <i class="fas fa-cubes"></i>
                                                </b-button>
                                                <b-button
                                                    v-if="canUpdateTarget && work.id"
                                                    size="sm"
                                                    class="object-lifecycle-button"
                                                    :variant="work.is_active === false ? 'outline-success' : 'outline-secondary'"
                                                    :title="work.is_active === false
                                                        ? 'Вернуть работу в мастер новых отчётов'
                                                        : 'Скрыть работу из новых отчётов, сохранив историю'"
                                                    v-b-tooltip.hover
                                                    :disabled="isTargetStatusChanging(work)"
                                                    @click="changeTargetStatus(work)"
                                                >
                                                    <b-spinner v-if="isTargetStatusChanging(work)" small></b-spinner>
                                                    <span v-else>{{ work.is_active === false ? 'Сделать действующей' : 'Отключить' }}</span>
                                                </b-button>
                                                <b-button
                                                    v-if="canUpdateTarget && work.work_id"
                                                    size="sm"
                                                    variant="outline-primary"
                                                    title="Изменить работу"
                                                    @click="openEditTargetModal(work)"
                                                >
                                                    <i class="fas fa-pen"></i>
                                                </b-button>
                                                <b-button
                                                    v-if="canDeleteTarget && work.work_id"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    title="Удалить работу"
                                                    @click="openDeleteTargetModal(work)"
                                                >
                                                    <i class="fas fa-trash-alt"></i>
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="object-progress-bar"
                                        :class="{ 'object-progress-bar--overrun': isWorkOverrun(work) }"
                                    >
                                        <span
                                            :class="progressBarClass(work.progress_percent)"
                                            :style="{ width: `${workProgressContractFillPercent(work)}%` }"
                                        ></span>
                                        <span
                                            v-if="isWorkOverrun(work)"
                                            class="object-progress-bar__fill object-progress-bar__fill--overrun"
                                            :style="{
                                                left: `${workProgressContractMarkerPercent(work)}%`,
                                                width: `${workProgressOverrunFillPercent(work)}%`
                                            }"
                                        ></span>
                                        <span
                                            class="object-progress-bar__contract-mark"
                                            :style="{ left: `${workProgressContractMarkerPercent(work)}%` }"
                                            title="Объем по договору"
                                        ></span>
                                    </div>
                                    <div class="object-work-progress-card__meta">
                                        <span>{{ work.progress_percent }}%</span>
                                        <span>{{ formatAmount(work.completed_value, work.unit) }} из {{ formatAmount(work.plan_value, work.unit) }}</span>
                                    </div>
                                    <button
                                        v-if="work.materials.length && (hasHiddenWorkMaterials(work) || isWorkMaterialsExpanded(work))"
                                        type="button"
                                        class="object-work-materials-toggle"
                                        @click="toggleWorkMaterials(work)"
                                    >
                                        <i :class="isWorkMaterialsExpanded(work) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                        {{ isWorkMaterialsExpanded(work) ? 'Скрыть материалы' : 'Показать все материалы' }}
                                        <span class="object-work-materials-toggle__count">{{ work.materials.length }}</span>
                                        <span
                                            v-if="workMaterialIssuesCount(work)"
                                            class="object-work-materials-toggle__count object-work-materials-toggle__count--danger"
                                            title="С отклонениями"
                                        >
                                            {{ workMaterialIssuesCount(work) }}
                                        </span>
                                    </button>
                                    <div
                                        v-if="visibleWorkMaterials(work).length"
                                        class="object-work-materials"
                                        :class="{ 'object-work-materials--issues': !isWorkMaterialsExpanded(work) && workProgressFilter === 'material_issues' }"
                                    >
                                        <div class="object-work-materials__title">
                                            <i :class="isWorkMaterialsExpanded(work) ? 'fas fa-cubes' : 'fas fa-exclamation-triangle'"></i>
                                            {{ isWorkMaterialsExpanded(work) ? 'Все материалы' : 'Отклонения материалов' }}
                                        </div>
                                        <div
                                            v-for="material in visibleWorkMaterials(work)"
                                            :key="material.id || material.name"
                                            class="object-work-material"
                                            :class="[
                                                `object-work-material--${material.status}`,
                                                {'object-work-material--disabled': material.is_available === false}
                                            ]"
                                        >
                                            <div class="object-work-material__header">
                                                <strong>{{ material.name }}</strong>
                                                <div class="object-work-material__side">
                                                    <b-badge :variant="materialStatusVariant(material)">
                                                        {{ material.status_label }}
                                                    </b-badge>
                                                    <b-badge v-if="material.is_active === false" variant="secondary">
                                                        Отключена
                                                    </b-badge>
                                                    <b-badge v-else-if="material.directory_is_active === false" variant="secondary">
                                                        Материал отключён в справочнике
                                                    </b-badge>
                                                    <div
                                                        v-if="material.target_material_id && (canUpdateMaterialTarget || canDeleteMaterialTarget)"
                                                        class="object-work-material__actions"
                                                    >
                                                        <b-button
                                                            v-if="canUpdateMaterialTarget"
                                                            size="sm"
                                                            class="object-lifecycle-button"
                                                            :variant="material.is_active === false ? 'outline-success' : 'outline-secondary'"
                                                            :title="material.is_active === false
                                                                ? 'Вернуть норму материала в мастер новых отчётов'
                                                                : 'Скрыть норму материала из новых отчётов, сохранив историю'"
                                                            v-b-tooltip.hover
                                                            :disabled="isMaterialTargetStatusChanging(material)"
                                                            @click="changeMaterialTargetStatus(material)"
                                                        >
                                                            <b-spinner v-if="isMaterialTargetStatusChanging(material)" small></b-spinner>
                                                            <span v-else>{{ material.is_active === false ? 'Сделать действующей' : 'Отключить' }}</span>
                                                        </b-button>
                                                        <b-button
                                                            v-if="canUpdateMaterialTarget"
                                                            size="sm"
                                                            variant="outline-primary"
                                                            title="Изменить материал"
                                                            @click="openEditMaterialTargetModal(work, material)"
                                                        >
                                                            <i class="fas fa-pen"></i>
                                                        </b-button>
                                                        <b-button
                                                            v-if="canDeleteMaterialTarget"
                                                            size="sm"
                                                            variant="outline-danger"
                                                            title="Удалить материал"
                                                            @click="openDeleteMaterialTargetModal(work, material)"
                                                        >
                                                            <i class="fas fa-trash-alt"></i>
                                                        </b-button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="object-material-scale" :title="materialScaleTitle(material)">
                                                <span class="object-material-scale__label object-material-scale__label--left">
                                                    Экономия
                                                </span>
                                                <span class="object-material-scale__label object-material-scale__label--norm">
                                                    По норме
                                                </span>
                                                <span class="object-material-scale__label object-material-scale__label--right">
                                                    Перерасход
                                                </span>
                                                <span
                                                    class="object-material-scale__norm"
                                                    :style="materialNormBandStyle(material)"
                                                ></span>
                                                <span
                                                    class="object-material-scale__tick"
                                                    :style="{ left: `${materialNormLeftPosition(material)}%` }"
                                                ></span>
                                                <span
                                                    class="object-material-scale__tick"
                                                    :style="{ left: `${materialNormRightPosition(material)}%` }"
                                                ></span>
                                                <span
                                                    :class="materialScaleFillClass(material)"
                                                    :style="{ width: `${materialScalePosition(material)}%` }"
                                                ></span>
                                                <span
                                                    :class="materialScaleMarkerClass(material)"
                                                    :style="{ left: `${materialScalePosition(material)}%` }"
                                                ></span>
                                            </div>
                                            <div class="object-work-material__meta">
                                                <span>По факту {{ formatAmount(material.fact_value, material.unit) }}</span>
                                                <span>По норме {{ formatAmount(material.norm_value, material.unit) }}</span>
                                                <span>{{ materialRatioText(material) }}</span>
                                                <span :class="{ 'object-work-material__meta-danger': material.limit_error }">
                                                    {{ materialDeviationText(material) }}
                                                </span>
                                                <span>{{ materialLimitText(material) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>

                            <div v-else class="object-work-progress-grid">
                                <article
                                    v-for="group in filteredWorkProgressGroups"
                                    :key="group.id"
                                    class="object-work-progress-card object-work-progress-card--group"
                                    :class="`object-work-progress-card--${group.status}`"
                                >
                                    <div class="object-work-progress-card__header">
                                        <div
                                            class="object-work-progress-card__title"
                                            :title="groupProgressTitle(group)"
                                        >
                                            <strong>{{ group.name }}</strong>
                                            <span>{{ group.works_count }} {{ pluralizeWorks(group.works_count) }}</span>
                                        </div>
                                        <div class="object-work-progress-card__side">
                                            <b-badge :variant="workProgressVariant(group)">
                                                {{ group.status_label }}
                                            </b-badge>
                                        </div>
                                    </div>
                                    <div
                                        class="object-progress-bar"
                                        :class="{ 'object-progress-bar--overrun': isWorkOverrun(group) }"
                                    >
                                        <span
                                            :class="progressBarClass(group.progress_percent)"
                                            :style="{ width: `${workProgressContractFillPercent(group)}%` }"
                                        ></span>
                                        <span
                                            v-if="isWorkOverrun(group)"
                                            class="object-progress-bar__fill object-progress-bar__fill--overrun"
                                            :style="{
                                                left: `${workProgressContractMarkerPercent(group)}%`,
                                                width: `${workProgressOverrunFillPercent(group)}%`
                                            }"
                                        ></span>
                                        <span
                                            class="object-progress-bar__contract-mark"
                                            :style="{ left: `${workProgressContractMarkerPercent(group)}%` }"
                                            title="Объем по договору"
                                        ></span>
                                    </div>
                                    <div class="object-work-progress-card__meta">
                                        <span>{{ group.progress_percent }}%</span>
                                        <span>{{ formatAmount(group.completed_value, group.unit) }} из {{ formatAmount(group.plan_value, group.unit) }}</span>
                                    </div>
                                    <div class="object-work-progress-card__details">
                                        <span>{{ group.completed_works_count }} завершено</span>
                                        <span>{{ group.in_progress_works_count }} в работе</span>
                                        <span v-if="group.work_overruns_count" class="text-danger">
                                            {{ group.work_overruns_count }} сверх договора
                                        </span>
                                        <span v-if="group.material_issues_count" class="text-danger">
                                            {{ group.material_issues_count }} отклонений материалов
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        class="object-work-materials-toggle"
                                        @click="toggleWorkGroup(group)"
                                    >
                                        <i :class="isWorkGroupExpanded(group) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                        {{ isWorkGroupExpanded(group) ? 'Скрыть работы' : 'Показать работы' }}
                                        <span class="object-work-materials-toggle__count">{{ group.works.length }}</span>
                                    </button>
                                    <div
                                        v-if="isWorkGroupExpanded(group)"
                                        class="object-work-group-works"
                                    >
                                        <div
                                            v-for="work in group.works"
                                            :key="work.id || work.work_id"
                                            class="object-work-group-work"
                                            :class="[
                                                `object-work-group-work--${work.status}`,
                                                {'object-work-group-work--disabled': work.is_available === false}
                                            ]"
                                        >
                                            <div class="object-work-group-work__header">
                                                <div
                                                    class="object-work-group-work__title"
                                                    :title="workHeaderTitle(work)"
                                                >
                                                    <strong>{{ work.name }}</strong>
                                                    <span v-if="progressDisplayText(work.project_name)">
                                                        {{ progressDisplayText(work.project_name) }}
                                                    </span>
                                                </div>
                                                <b-badge :variant="workProgressVariant(work)">
                                                    {{ work.status_label }}
                                                </b-badge>
                                                <b-badge v-if="work.is_active === false" variant="secondary">
                                                    Отключена
                                                </b-badge>
                                                <b-badge v-else-if="work.directory_is_active === false" variant="secondary">
                                                    Вид работ отключён в справочнике
                                                </b-badge>
                                            </div>
                                            <div
                                                class="object-progress-bar object-progress-bar--sm"
                                                :class="{ 'object-progress-bar--overrun': isWorkOverrun(work) }"
                                            >
                                                <span
                                                    :class="progressBarClass(work.progress_percent)"
                                                    :style="{ width: `${workProgressContractFillPercent(work)}%` }"
                                                ></span>
                                                <span
                                                    v-if="isWorkOverrun(work)"
                                                    class="object-progress-bar__fill object-progress-bar__fill--overrun"
                                                    :style="{
                                                        left: `${workProgressContractMarkerPercent(work)}%`,
                                                        width: `${workProgressOverrunFillPercent(work)}%`
                                                    }"
                                                ></span>
                                                <span
                                                    class="object-progress-bar__contract-mark"
                                                    :style="{ left: `${workProgressContractMarkerPercent(work)}%` }"
                                                    title="Объем по договору"
                                                ></span>
                                            </div>
                                            <div class="object-work-group-work__meta">
                                                <span>{{ work.progress_percent }}%</span>
                                                <span>{{ formatAmount(work.completed_value, work.unit) }} из {{ formatAmount(work.plan_value, work.unit) }}</span>
                                            </div>
                                            <button
                                                v-if="work.materials.length"
                                                type="button"
                                                class="object-work-materials-toggle object-work-materials-toggle--nested"
                                                @click="toggleWorkMaterials(work)"
                                            >
                                                <i :class="isWorkMaterialsExpanded(work) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                                {{ isWorkMaterialsExpanded(work) ? 'Скрыть материалы' : 'Показать материалы' }}
                                                <span class="object-work-materials-toggle__count">{{ work.materials.length }}</span>
                                                <span
                                                    v-if="workMaterialIssuesCount(work)"
                                                    class="object-work-materials-toggle__count object-work-materials-toggle__count--danger"
                                                    title="С отклонениями"
                                                >
                                                    {{ workMaterialIssuesCount(work) }}
                                                </span>
                                            </button>
                                            <div
                                                v-if="isWorkMaterialsExpanded(work) && work.materials.length"
                                                class="object-work-materials object-work-materials--nested"
                                            >
                                                <div class="object-work-materials__title">
                                                    <i class="fas fa-cubes"></i>
                                                    Материалы
                                                </div>
                                                <div
                                                    v-for="material in work.materials"
                                                    :key="material.id || material.name"
                                                    class="object-work-material"
                                                    :class="[
                                                        `object-work-material--${material.status}`,
                                                        {'object-work-material--disabled': material.is_available === false}
                                                    ]"
                                                >
                                                    <div class="object-work-material__header">
                                                        <strong>{{ material.name }}</strong>
                                                        <div class="object-work-material__side">
                                                            <b-badge :variant="materialStatusVariant(material)">
                                                                {{ material.status_label }}
                                                            </b-badge>
                                                            <b-badge v-if="material.is_active === false" variant="secondary">
                                                                Отключена
                                                            </b-badge>
                                                            <b-badge v-else-if="material.directory_is_active === false" variant="secondary">
                                                                Материал отключён в справочнике
                                                            </b-badge>
                                                        </div>
                                                    </div>
                                                    <div class="object-material-scale" :title="materialScaleTitle(material)">
                                                        <span class="object-material-scale__label object-material-scale__label--left">
                                                            Экономия
                                                        </span>
                                                        <span class="object-material-scale__label object-material-scale__label--norm">
                                                            По норме
                                                        </span>
                                                        <span class="object-material-scale__label object-material-scale__label--right">
                                                            Перерасход
                                                        </span>
                                                        <span
                                                            class="object-material-scale__norm"
                                                            :style="materialNormBandStyle(material)"
                                                        ></span>
                                                        <span
                                                            class="object-material-scale__tick"
                                                            :style="{ left: `${materialNormLeftPosition(material)}%` }"
                                                        ></span>
                                                        <span
                                                            class="object-material-scale__tick"
                                                            :style="{ left: `${materialNormRightPosition(material)}%` }"
                                                        ></span>
                                                        <span
                                                            :class="materialScaleFillClass(material)"
                                                            :style="{ width: `${materialScalePosition(material)}%` }"
                                                        ></span>
                                                        <span
                                                            :class="materialScaleMarkerClass(material)"
                                                            :style="{ left: `${materialScalePosition(material)}%` }"
                                                        ></span>
                                                    </div>
                                                    <div class="object-work-material__meta">
                                                        <span>По факту {{ formatAmount(material.fact_value, material.unit) }}</span>
                                                        <span>По норме {{ formatAmount(material.norm_value, material.unit) }}</span>
                                                        <span>{{ materialRatioText(material) }}</span>
                                                        <span :class="{ 'object-work-material__meta-danger': material.limit_error }">
                                                            {{ materialDeviationText(material) }}
                                                        </span>
                                                        <span>{{ materialLimitText(material) }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </b-collapse>
                </section>

                <ObjectGalleryBlock
                    v-if="canReadGallery && buildingProcessId"
                    :process-id="buildingProcessId"
                />
            </div>

            <div v-else class="object-card-layout">
                <div class="object-card-main">
                    <section class="object-panel">
                    <div class="object-panel__header">
                        <h6>Основные данные</h6>
                    </div>
                    <div class="object-form-grid">
                        <b-form-group label="Название" label-for="object-name">
                            <b-form-input
                                id="object-name"
                                v-model.trim="form.name"
                                size="sm"
                                :disabled="!canEditObject"
                                :state="nameState"
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group label="Телефон" label-for="object-phone">
							<phone-input
                                id="object-phone"
								v-model="form.phone"
                                size="sm"
                                :disabled="!canEditObject"
							></phone-input>
                        </b-form-group>

                        <b-form-group label="Страна" label-for="object-country">
                            <b-form-select
                                id="object-country"
                                v-model="form.country"
                                :options="countryOptions"
                                size="sm"
                                :disabled="!canEditObject"
                            ></b-form-select>
                        </b-form-group>

                        <b-form-group label="Адрес" label-for="object-address" class="object-form-grid__wide">
                            <b-form-input
                                id="object-address"
                                v-model.trim="form.address"
                                size="sm"
                                :disabled="!canEditObject"
                            ></b-form-input>
                        </b-form-group>
                    </div>
                    </section>

                    <section class="object-panel">
                    <div class="object-panel__header object-panel__header--split">
                        <h6>Рабочий процесс</h6>
                        <b-form-checkbox
                            v-if="!hasProcess"
                            v-model="processEnabled"
                            switch
                            :disabled="!canCreateProcess"
                        >
                            Создать процесс
                        </b-form-checkbox>
                    </div>

                    <div v-if="!processEnabled && !hasProcess" class="text-muted py-2">
                        Объект хранится как строительный объект без рабочего процесса.
                    </div>

                    <div v-else class="object-form-grid">
                        <b-form-group label="Действующий" label-for="process-active">
                            <b-form-checkbox
                                id="process-active"
                                v-model="processForm.is_active"
                                switch
                                :disabled="!canEditProcess"
                                @change="onActiveChange"
                            >
                                {{ processForm.is_active ? 'В работе' : 'В архиве' }}
                            </b-form-checkbox>
                        </b-form-group>

                        <b-form-group label="Напоминания" label-for="process-notification">
                            <b-form-checkbox
                                id="process-notification"
                                v-model="processForm.need_notification"
                                switch
                                :disabled="!canEditProcess || !processForm.is_active"
                            >
                                Включены
                            </b-form-checkbox>
                        </b-form-group>

                        <b-form-group label="Прораб" label-for="process-staff">
                            <v-select
                                id="process-staff"
                                label="full_name"
                                :value="users.find(user => Number(user.user_id) === Number(processForm.main_staff_id))"
                                :options="users"
                                :clearable="true"
                                :disabled="!canEditProcess"
                                @input="value => processForm.main_staff_id = value ? value.user_id : null"
                            ></v-select>
                        </b-form-group>

                        <b-form-group label="Куратор" label-for="process-curator">
                            <v-select
                                id="process-curator"
                                label="full_name"
                                :value="users.find(user => Number(user.user_id) === Number(processForm.curator_id))"
                                :options="users"
                                :clearable="true"
                                :disabled="!canEditProcess"
                                @input="value => processForm.curator_id = value ? value.user_id : null"
                            ></v-select>
                        </b-form-group>

                        <b-form-group label="Имеющие доступ" class="object-form-grid__wide">
                            <v-select
                                label="full_name"
                                :value="selectedHasAccessUsers"
                                :options="availableUsersForHasAccess"
                                :clearable="true"
                                :disabled="!canEditProcess"
                                multiple
                                @input="value => processForm.has_access = value ? value.map(user => user.user_id) : []"
                            ></v-select>
                        </b-form-group>

                        <b-form-group label="Время подачи отчета" label-for="process-report-time">
                            <b-form-timepicker
                                id="process-report-time"
                                v-model="processForm.report_time"
                                size="sm"
                                :disabled="!canEditProcess"
                            ></b-form-timepicker>
                        </b-form-group>

                        <b-form-group label="ID основной группы Telegram" label-for="process-chat" description="Создание и привязка доступны в разделе «Telegram-группы» карточки.">
                            <b-form-input
                                id="process-chat"
                                v-model.trim="processForm.common_chat_id"
                                size="sm"
                                type="number"
                                readonly
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group label="Номер договора" label-for="process-contract-name">
                            <b-form-input
                                id="process-contract-name"
                                v-model.trim="processForm.contract_name"
                                size="sm"
                                :disabled="!canEditProcess"
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group label="Дата договора" label-for="process-contract-date">
                            <DatePicker
                                v-if="canEditProcess"
                                id="process-contract-date"
                                v-model="processForm.contract_date"
                            />
                            <b-form-input
                                v-else
                                id="process-contract-date"
                                :value="formatDate(processForm.contract_date)"
                                size="sm"
                                disabled
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group
                            v-if="isAds"
                            label="Рабочая папка объекта"
                            label-for="process-folder"
                            class="object-form-grid__wide"
                        >
                            <b-form-input
                                id="process-folder"
                                v-model.trim="processForm.folder_path"
                                size="sm"
                                :disabled="!canEditProcess"
                                lazy-formatter
                                :formatter="folderFormatter"
                            ></b-form-input>
                        </b-form-group>

                        <div class="object-form-grid__wide">
                            <label class="d-block">Доступ к галерее</label>
                            <MultipleParticipantsConfig
                                v-if="canEditProcess"
                                v-model="processForm.gallery_access_config"
                                :users="participantUsers"
                                :departments="participantDepartments"
                                participant-label="получателя доступа"
                                :show-object-roles="true"
                                :allowed-types="galleryObjectAccessTypes"
                                :object-role-options="galleryObjectRoleOptions"
                                :show-fallback-option="false"
                                :show-priority="false"
                                empty-text="Отдельный доступ к галерее объекта не настроен"
                                hint-text="Эти участники видят фотографии только этого объекта"
                            />
                            <div v-else class="object-readonly-box text-muted">
                                {{ galleryAccessSummary }}
                            </div>
                        </div>
                    </div>
                    </section>
                </div>
            </div>

            <div
                v-if="!isCreate && card"
                class="object-mobile-tab-grid"
                role="tablist"
                aria-label="Разделы карточки объекта"
            >
                <button
                    v-for="tab in mobileTabCards"
                    :key="tab.key"
                    type="button"
                    class="object-mobile-tab-card"
                    :class="{
                        'object-mobile-tab-card--active': activeTabIndex === tab.index,
                        'object-mobile-tab-card--error': tab.error
                    }"
                    role="tab"
                    :aria-selected="String(activeTabIndex === tab.index)"
                    :title="tab.error ? 'Не удалось загрузить данные раздела' : tab.label"
                    @click="activeTabIndex = tab.index"
                >
                    <i :class="tab.icon" aria-hidden="true"></i>
                    <span class="object-mobile-tab-card__label">{{ tab.label }}</span>
                    <span class="object-mobile-tab-card__status">
                        <b-spinner v-if="tab.loading" small></b-spinner>
                        <i v-else-if="tab.error" class="fas fa-exclamation-circle" aria-hidden="true"></i>
                        <strong v-else>{{ tab.count }}</strong>
                        <small v-if="!tab.loading && !tab.error && tab.annulledCount">
                            {{ tab.annulledCount }} аннул.
                        </small>
                    </span>
                </button>
            </div>

            <b-tabs
                v-if="!isCreate && card"
                ref="relatedTabs"
                v-model="activeTabIndex"
                small
                tabindex="-1"
                class="object-card-tabs object-summary-target"
            >
                <b-tab>
                    <template #title>
                        История
                    </template>
                    <ObjectActivityBlock
                        ref="activityBlock"
                        :activity="activity"
                        :can-edit="canComment"
                        :object-id="card.building_id"
                        :users="users"
                        :loading="activityLoading"
                        :submitting="commentSubmitting"
                        @addComment="addComment"
                        @editComment="editComment"
                        @removeComment="removeComment"
                        @removeFile="removeFile"
                        @loadMore="loadMoreActivity"
                    />
                </b-tab>
                <b-tab>
                    <template #title>
                        Заявки
                        <b-badge v-if="relatedRequestsTotal" variant="primary" class="ml-1">
                            {{ relatedRequestsTotal }}
                        </b-badge>
                    </template>
                    <div class="object-related-list">
                        <div class="object-related-list__header">
                            <div>
                                <h6>Заявки объекта</h6>
                                <small class="text-muted">
                                    {{ relatedRequestsTotalText }}
                                </small>
                            </div>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                :disabled="!buildingProcessId"
                                @click="openRequests"
                            >
                                Открыть в заявках
                            </b-button>
                        </div>

                        <div v-if="!buildingProcessId" class="object-empty-state">
                            <i class="fas fa-clipboard-list"></i>
                            <span>Для заявок нужен рабочий процесс объекта</span>
                        </div>
                        <div v-else-if="relatedRequestsLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка заявок
                        </div>
                        <b-alert v-else-if="relatedRequestsError" show variant="danger">
                            {{ relatedRequestsError }}
                        </b-alert>
                        <ObjectRelatedAccessNotice
                            v-else-if="relatedRequestsAccessLimited && !relatedRequests.length"
                            empty
                            entity-label="заявки"
                        />
                        <div v-else>
                            <ObjectRelatedAccessNotice
                                v-if="relatedRequestsAccessLimited"
                                entity-label="заявки"
                            />
                            <div v-if="relatedRequests.length">
                                <RequestShortItem
                                    v-for="request in relatedRequests"
                                    :key="request.id"
                                    :request="request"
                                />
                            </div>
                            <div v-else class="text-muted text-center py-3">
                                Заявок по объекту пока нет
                            </div>
                        </div>
                    </div>
                </b-tab>
                <b-tab>
                    <template #title>
                        Задачи
                        <b-badge v-if="relatedTasks.length" variant="info" class="ml-1">
                            {{ relatedTasks.length }}
                        </b-badge>
                    </template>
                    <div class="object-related-list">
                        <div class="object-related-list__header">
                            <div>
                                <h6>Задачи объекта</h6>
                                <small class="text-muted">
                                    {{ relatedTasksTotalText }}
                                </small>
                            </div>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                :disabled="!buildingProcessId"
                                @click="openTasks"
                            >
                                Открыть в задачах
                            </b-button>
                        </div>

                        <div v-if="!buildingProcessId" class="object-empty-state">
                            <i class="fas fa-bell"></i>
                            <span>Для задач нужен рабочий процесс объекта</span>
                        </div>
                        <div v-else-if="relatedTasksLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка задач
                        </div>
                        <b-alert v-else-if="relatedTasksError" show variant="danger">
                            {{ relatedTasksError }}
                        </b-alert>
                        <ObjectRelatedAccessNotice
                            v-else-if="relatedTasksAccessLimited && !relatedTasks.length"
                            empty
                            entity-label="задачи"
                        />
                        <div v-else>
                            <ObjectRelatedAccessNotice
                                v-if="relatedTasksAccessLimited"
                                entity-label="задачи"
                            />
                            <div v-if="relatedTasks.length">
                                <TaskShortItem
                                    v-for="task in sortedRelatedTasks"
                                    :key="task.id"
                                    :task="task"
                                />
                            </div>
                            <div v-else class="text-muted text-center py-3">
                                Задач по объекту пока нет
                            </div>
                        </div>
                    </div>
                </b-tab>
                <b-tab>
                    <template #title>
                        Топливо
                        <b-badge v-if="objectFuelRows.length" variant="warning" class="ml-1">
                            {{ objectFuelRows.length }}
                        </b-badge>
                    </template>
                    <div class="object-related-list">
                        <div class="object-related-list__header">
                            <div>
                                <h6>Израсходованное топливо</h6>
                                <small class="text-muted">
                                    Строк по объекту: {{ objectFuelRows.length }}
                                </small>
                            </div>
                        </div>

                        <div v-if="!buildingProcessId" class="object-empty-state">
                            <i class="fas fa-gas-pump"></i>
                            <span>Для отчета по топливу нужен рабочий процесс объекта</span>
                        </div>
                        <div v-else-if="consumptionLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка топлива
                        </div>
                        <b-alert v-else-if="consumptionError" show variant="danger">
                            {{ consumptionError }}
                        </b-alert>
                        <div v-else-if="objectFuelRows.length">
                            <div class="object-desktop-data-table">
                                <b-table
                                    :items="objectFuelRows"
                                    :fields="fuelConsumptionFields"
                                    small
                                    striped
                                    responsive="lg"
                                    class="object-consumption-table"
                                >
                                    <template #cell(date_time_begin)="data">
                                        {{ formatDateTime(data.item.date_time_begin) }}
                                    </template>
                                    <template #cell(date_time_end)="data">
                                        {{ formatDateTime(data.item.date_time_end) }}
                                    </template>
                                    <template #cell(value)="data">
                                        {{ numberText(data.item.value) }}
                                    </template>
                                    <template #cell(notes)="data">
                                        <ul v-if="splitConsumptionNotes(data.item.notes).length" class="object-consumption-notes">
                                            <li v-for="note in splitConsumptionNotes(data.item.notes)" :key="note">
                                                {{ note }}
                                            </li>
                                        </ul>
                                        <span v-else>-</span>
                                    </template>
                                </b-table>
                            </div>
                            <div class="object-mobile-data-list">
                                <article
                                    v-for="(row, index) in objectFuelRows"
                                    :key="`fuel_${row.report_id || index}_${index}`"
                                    class="object-mobile-data-card"
                                >
                                    <div class="object-mobile-data-card__header">
                                        <div>
                                            <small>Вид топлива</small>
                                            <strong>{{ row.fuel_name || 'Не указан' }}</strong>
                                        </div>
                                        <b-badge variant="warning">
                                            {{ numberText(row.value) }} {{ row.unit || '' }}
                                        </b-badge>
                                    </div>
                                    <div class="object-mobile-data-card__meta">
                                        <span>
                                            <small>Отчёт</small>
                                            <router-link v-if="row.report_id" :to="`/reports/${row.report_id}`">
                                                №{{ row.report_id }}
                                            </router-link>
                                            <strong v-else>—</strong>
                                        </span>
                                        <span>
                                            <small>Начало смены</small>
                                            <strong>{{ formatDateTime(row.date_time_begin) || '—' }}</strong>
                                        </span>
                                        <span>
                                            <small>Окончание смены</small>
                                            <strong>{{ formatDateTime(row.date_time_end) || '—' }}</strong>
                                        </span>
                                    </div>
                                    <div v-if="splitConsumptionNotes(row.notes).length" class="object-mobile-data-card__details">
                                        <small>Техника</small>
                                        <ul class="object-consumption-notes">
                                            <li v-for="note in splitConsumptionNotes(row.notes)" :key="note">
                                                {{ note }}
                                            </li>
                                        </ul>
                                    </div>
                                </article>
                            </div>
                        </div>
                        <div v-else class="text-muted text-center py-3">
                            Топливо по объекту пока не отражалось
                        </div>
                    </div>
                </b-tab>
                <b-tab>
                    <template #title>
                        Материалы
                        <b-badge v-if="objectMaterialRows.length" variant="success" class="ml-1">
                            {{ objectMaterialRows.length }}
                        </b-badge>
                    </template>
                    <div class="object-related-list">
                        <div class="object-related-list__header">
                            <div>
                                <h6>Израсходованные материалы</h6>
                                <small class="text-muted">
                                    Строк по объекту: {{ objectMaterialRows.length }}
                                </small>
                            </div>
                        </div>

                        <div v-if="!buildingProcessId" class="object-empty-state">
                            <i class="fas fa-cubes"></i>
                            <span>Для отчета по материалам нужен рабочий процесс объекта</span>
                        </div>
                        <div v-else-if="consumptionLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка материалов
                        </div>
                        <b-alert v-else-if="consumptionError" show variant="danger">
                            {{ consumptionError }}
                        </b-alert>
                        <div v-else-if="objectMaterialRows.length">
                            <div class="object-desktop-data-table">
                                <b-table
                                    :items="objectMaterialRows"
                                    :fields="materialConsumptionFields"
                                    small
                                    striped
                                    responsive="lg"
                                    class="object-consumption-table"
                                >
                                    <template #cell(date_time_begin)="data">
                                        {{ formatDateTime(data.item.date_time_begin) }}
                                    </template>
                                    <template #cell(date_time_end)="data">
                                        {{ formatDateTime(data.item.date_time_end) }}
                                    </template>
                                    <template #cell(work_value)="data">
                                        {{ numberText(data.item.work_value) }}
                                    </template>
                                    <template #cell(material_value)="data">
                                        {{ numberText(data.item.material_value) }}
                                    </template>
                                </b-table>
                            </div>
                            <div class="object-mobile-data-list">
                                <article
                                    v-for="(row, index) in objectMaterialRows"
                                    :key="`material_${row.report_id || index}_${index}`"
                                    class="object-mobile-data-card"
                                >
                                    <div class="object-mobile-data-card__header">
                                        <div>
                                            <small>Материал</small>
                                            <strong>{{ row.material_name || 'Не указан' }}</strong>
                                        </div>
                                        <b-badge variant="success">
                                            {{ numberText(row.material_value) }} {{ row.material_unit || '' }}
                                        </b-badge>
                                    </div>
                                    <div class="object-mobile-data-card__section">
                                        <small>Работа</small>
                                        <strong>{{ row.work_name || 'Не указана' }}</strong>
                                        <span v-if="row.work_project_name">{{ row.work_project_name }}</span>
                                    </div>
                                    <div class="object-mobile-data-card__meta">
                                        <span>
                                            <small>Отчёт</small>
                                            <router-link v-if="row.report_id" :to="`/reports/${row.report_id}`">
                                                №{{ row.report_id }}
                                            </router-link>
                                            <strong v-else>—</strong>
                                        </span>
                                        <span>
                                            <small>Начало смены</small>
                                            <strong>{{ formatDateTime(row.date_time_begin) || '—' }}</strong>
                                        </span>
                                        <span>
                                            <small>Окончание смены</small>
                                            <strong>{{ formatDateTime(row.date_time_end) || '—' }}</strong>
                                        </span>
                                        <span>
                                            <small>Объём работ</small>
                                            <strong>{{ numberText(row.work_value) }} {{ row.work_unit || '' }}</strong>
                                        </span>
                                    </div>
                                </article>
                            </div>
                        </div>
                        <div v-else class="text-muted text-center py-3">
                            Материалы по объекту пока не отражались
                        </div>
                    </div>
                </b-tab>
                <b-tab>
                    <template #title>
                        Отчеты
                        <b-badge v-if="relatedReportsTotal" variant="secondary" class="ml-1">
                            {{ relatedReportsTotal }}
                        </b-badge>
                        <b-badge v-if="relatedReportCounts.annulled" variant="danger" class="ml-1">
                            Аннулировано: {{ relatedReportCounts.annulled }}
                        </b-badge>
                    </template>
                    <div class="object-related-list">
                        <div class="object-related-list__header">
                            <div>
                                <h6>Отчёты объекта</h6>
                                <small class="text-muted">
                                    {{ relatedReportsTotalText }}
                                </small>
                            </div>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                :disabled="!buildingProcessId"
                                @click="openReports"
                            >
                                Открыть в отчётах
                            </b-button>
                        </div>

                        <b-button-group
                            size="sm"
                            class="object-report-status-filter mb-3"
                            aria-label="Статус отчётов объекта"
                        >
                            <b-button
                                v-for="option in relatedReportStatusOptions"
                                :key="option.value"
                                :variant="relatedReportStatus === option.value ? option.activeVariant : 'outline-secondary'"
                                @click="setRelatedReportStatus(option.value)"
                            >
                                {{ option.text }} ({{ option.count }})
                            </b-button>
                        </b-button-group>

                        <div v-if="!buildingProcessId" class="object-empty-state">
                            <i class="fas fa-flag-checkered"></i>
                            <span>Для отчётов нужен рабочий процесс объекта</span>
                        </div>
                        <div v-else-if="relatedReportsLoading" class="text-center text-muted py-3">
                            <b-spinner small class="mr-1"></b-spinner>
                            Загрузка отчётов
                        </div>
                        <b-alert v-else-if="relatedReportsError" show variant="danger">
                            {{ relatedReportsError }}
                        </b-alert>
                        <ObjectRelatedAccessNotice
                            v-else-if="relatedReportsAccessLimited && !relatedReports.length"
                            empty
                            entity-label="отчёты"
                        />
                        <div v-else>
                            <ObjectRelatedAccessNotice
                                v-if="relatedReportsAccessLimited"
                                entity-label="отчёты"
                            />
                            <div v-if="relatedReports.length">
                                <div class="object-desktop-data-table">
                                    <b-table
                                        :items="relatedReports"
                                        :fields="relatedReportsFields"
                                        small
                                        hover
                                        responsive="lg"
                                        class="object-related-reports-table"
                                        :tbody-tr-class="relatedReportRowClass"
                                        @row-clicked="openReport"
                                    >
                                        <template #cell(shift_id)="data">
                                            <router-link
                                                :to="`/reports/${data.item.shift_id}`"
                                                class="font-weight-bold"
                                                @click.native.stop
                                            >
                                                №{{ data.item.shift_id }}
                                            </router-link>
                                        </template>
                                        <template #cell(date_time_begin)="data">
                                            <div>{{ formatDateTime(data.item.date_time_begin) }}</div>
                                            <small class="text-muted">до {{ formatDateTime(data.item.date_time_end) }}</small>
                                        </template>
                                        <template #cell(date_time_send)="data">
                                            <span
                                                v-if="data.item.date_time_send"
                                                :class="data.item.flags && data.item.flags.late ? 'text-danger font-weight-bold' : ''"
                                            >
                                                {{ formatDateTime(data.item.date_time_send) }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                        </template>
                                        <template #cell(media)="data">
                                            <span>
                                                <i class="fas fa-photo-video text-muted"></i>
                                                {{ data.item.stats ? data.item.stats.media_count : 0 }}
                                            </span>
                                        </template>
                                        <template #cell(issues)="data">
                                            <ReportBadges :report="data.item" compact show-empty />
                                        </template>
                                    </b-table>
                                </div>
                                <div class="object-mobile-data-list">
                                    <button
                                        v-for="report in relatedReports"
                                        :key="report.shift_id"
                                        type="button"
                                        class="object-mobile-data-card object-mobile-report-card"
                                        :class="{'object-mobile-report-card--annulled': report.is_annulled}"
                                        @click="openReport(report)"
                                    >
                                        <div class="object-mobile-data-card__header">
                                            <div>
                                                <small>Отчёт</small>
                                                <strong>№{{ report.shift_id }}</strong>
                                            </div>
                                            <div class="object-mobile-report-card__badges">
                                                <b-badge v-if="report.is_annulled" variant="danger">Аннулирован</b-badge>
                                                <b-badge variant="light">
                                                    <i class="fas fa-photo-video mr-1"></i>
                                                    {{ report.stats ? report.stats.media_count : 0 }}
                                                </b-badge>
                                            </div>
                                        </div>
                                        <div class="object-mobile-data-card__meta">
                                            <span>
                                                <small>Начало смены</small>
                                                <strong>{{ formatDateTime(report.date_time_begin) || '—' }}</strong>
                                            </span>
                                            <span>
                                                <small>Окончание смены</small>
                                                <strong>{{ formatDateTime(report.date_time_end) || '—' }}</strong>
                                            </span>
                                            <span>
                                                <small>Подан</small>
                                                <strong :class="report.flags && report.flags.late ? 'text-danger' : ''">
                                                    {{ formatDateTime(report.date_time_send) || '—' }}
                                                </strong>
                                            </span>
                                            <span>
                                                <small>Автор</small>
                                                <strong>{{ report.author_name || '—' }}</strong>
                                            </span>
                                        </div>
                                        <div class="object-mobile-report-card__indicators">
                                            <ReportBadges :report="report" compact show-empty />
                                        </div>
                                    </button>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center py-3">
                                {{ relatedReportStatus === 'annulled' ? 'Аннулированных отчётов по объекту нет' : 'Отчётов по объекту пока нет' }}
                            </div>
                        </div>
                    </div>
                </b-tab>
            </b-tabs>
        </div>

        <b-modal
            id="object-section-object-modal"
            title="Основные данные"
            ok-title="Сохранить"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="sectionSaving || !canSaveObjectSection"
            no-close-on-backdrop
            @ok="saveObjectSection"
            @hidden="clearSectionError"
        >
            <b-alert v-if="sectionError" show variant="danger">
                {{ sectionError }}
            </b-alert>

            <div class="object-form-grid">
                <b-form-group label="Название" label-for="object-draft-name" class="object-form-grid__wide">
                    <b-form-input
                        id="object-draft-name"
                        v-model.trim="objectDraftForm.name"
                        size="sm"
                        :disabled="sectionSaving"
                        :state="objectDraftNameState"
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Телефон" label-for="object-draft-phone">
					<phone-input
                        id="object-draft-phone"
						v-model="objectDraftForm.phone"
                        size="sm"
                        :disabled="sectionSaving"
					></phone-input>
                </b-form-group>

                <b-form-group label="Страна" label-for="object-draft-country">
                    <b-form-select
                        id="object-draft-country"
                        v-model="objectDraftForm.country"
                        :options="countryOptions"
                        size="sm"
                        :disabled="sectionSaving"
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Адрес" label-for="object-draft-address" class="object-form-grid__wide">
                    <b-form-input
                        id="object-draft-address"
                        v-model.trim="objectDraftForm.address"
                        size="sm"
                        :disabled="sectionSaving"
                    ></b-form-input>
                </b-form-group>
            </div>
        </b-modal>

        <b-modal
            id="object-section-process-modal"
            content-class="argus-glass-dialog"
            scrollable
            title="Рабочий процесс"
            ok-title="Сохранить"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="sectionSaving || !canSaveProcessSection"
            no-close-on-backdrop
            @ok="event => saveProcessSection(event, `object-section-process-modal`, `Рабочий процесс сохранен`)"
            @hidden="clearSectionError"
        >
            <b-alert v-if="sectionError" show variant="danger">
                {{ sectionError }}
            </b-alert>

            <b-form-checkbox
                v-if="!hasProcess"
                v-model="processDraftEnabled"
                switch
                :disabled="sectionSaving || !canCreateProcess"
                class="mb-3"
            >
                Создать рабочий процесс
            </b-form-checkbox>

            <div v-if="processDraftEnabled" class="object-form-grid">
                <b-form-group label="Действующий" label-for="process-draft-active">
                    <b-form-checkbox
                        id="process-draft-active"
                        v-model="processDraftForm.is_active"
                        switch
                        :disabled="sectionSaving"
                        @change="onDraftActiveChange"
                    >
                        {{ processDraftForm.is_active ? 'В работе' : 'В архиве' }}
                    </b-form-checkbox>
                </b-form-group>

                <b-form-group label="Напоминания" label-for="process-draft-notification">
                    <b-form-checkbox
                        id="process-draft-notification"
                        v-model="processDraftForm.need_notification"
                        switch
                        :disabled="sectionSaving || !processDraftForm.is_active"
                    >
                        Включены
                    </b-form-checkbox>
                </b-form-group>

                <b-form-group label="Время подачи отчета" label-for="process-draft-report-time">
                    <b-form-timepicker
                        id="process-draft-report-time"
                        v-model="processDraftForm.report_time"
                        size="sm"
                        :disabled="sectionSaving"
                    ></b-form-timepicker>
                </b-form-group>
            </div>
            <div v-else class="text-muted">
                Рабочий процесс не будет создан.
            </div>
            <template #modal-footer="{cancel}">
                <div class="w-100">
                    <div v-if="!processDraftForm.is_active && (!card.process || card.process.is_active !== false)" class="mb-2">
                        <div v-if="archiveChecking" role="status">Проверяем зарплатные периоды…</div>
                        <b-alert v-if="archiveCheckError" show variant="danger">
                            {{ archiveCheckError }}
                            <b-button size="sm" variant="outline-danger" :disabled="archiveChecking" @click="loadArchiveCheck">Повторить</b-button>
                        </b-alert>
                        <template v-if="archiveCheck">
                            <b-alert v-if="!archiveCheck.can_archive" show variant="danger">
                                Нельзя отправить объект в архив: не закрыты зарплатные периоды.
                                <ul class="mb-0" style="max-height: 18vh; overflow-y: auto">
                                    <li v-for="period in archiveCheck.blocking_periods" :key="period.month">
                                        {{ archiveDate(period.month) }} — {{ period.status_label }}
                                    </li>
                                </ul>
                                <b-button size="sm" variant="outline-danger" :disabled="archiveChecking" @click="loadArchiveCheck">Проверить снова</b-button>
                            </b-alert>
                            <p class="small mb-2" role="status">
                                Рекомендуем архивировать объект
                                <template v-if="archiveCheck.recommended_archive_date">не раньше {{ archiveDate(archiveCheck.recommended_archive_date) }}:</template>
                                после последнего отчёта должен пройти полный календарный месяц для завершения табелей, отчётов и расчёта зарплаты.
                            </p>
                        </template>
                    </div>
                    <b-alert v-if="sectionError" show variant="danger" role="alert">{{ sectionError }}</b-alert>
                    <div class="d-flex justify-content-end flex-wrap">
                        <b-button variant="secondary" :disabled="sectionSaving" @click="cancel">Отмена</b-button>
                        <b-button class="ml-2" variant="success"
                            :disabled="sectionSaving || !canSaveProcessSection || archiveChecking || (!processDraftForm.is_active && (!card.process || card.process.is_active !== false) && (!archiveCheck || !archiveCheck.can_archive))"
                            @click="saveProcessSection(null, 'object-section-process-modal', 'Рабочий процесс сохранен')">Сохранить</b-button>
                    </div>
                </div>
            </template>
        </b-modal>

        <b-modal
            id="object-section-contract-modal"
            title="Договор и связи"
            ok-title="Сохранить"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="sectionSaving || !canSaveProcessSection"
            no-close-on-backdrop
            @ok="event => saveProcessSection(event, `object-section-contract-modal`, `Договор и связи сохранены`)"
            @hidden="clearSectionError"
        >
            <b-alert v-if="sectionError" show variant="danger">
                {{ sectionError }}
            </b-alert>

            <div v-if="processDraftEnabled" class="object-form-grid">
                <b-form-group label="Номер договора" label-for="process-draft-contract-name">
                    <b-form-input
                        id="process-draft-contract-name"
                        v-model.trim="processDraftForm.contract_name"
                        size="sm"
                        :disabled="sectionSaving"
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Дата договора" label-for="process-draft-contract-date">
                    <DatePicker
                        id="process-draft-contract-date"
                        v-model="processDraftForm.contract_date"
                    />
                </b-form-group>

                <b-form-group label="ID основной группы Telegram" label-for="process-draft-chat" description="Создание и привязка доступны в разделе «Telegram-группы» карточки.">
                    <b-form-input
                        id="process-draft-chat"
                        v-model.trim="processDraftForm.common_chat_id"
                        size="sm"
                        type="number"
                        readonly
                    ></b-form-input>
                </b-form-group>

                <b-form-group
                    v-if="isAds"
                    label="Рабочая папка объекта"
                    label-for="process-draft-folder"
                    class="object-form-grid__wide"
                >
                    <b-form-input
                        id="process-draft-folder"
                        v-model.trim="processDraftForm.folder_path"
                        size="sm"
                        :disabled="sectionSaving"
                        lazy-formatter
                        :formatter="folderFormatter"
                    ></b-form-input>
                </b-form-group>
            </div>
        </b-modal>

        <b-modal
            id="object-section-team-modal"
            title="Команда и доступ"
            ok-title="Сохранить"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="sectionSaving || !canSaveProcessSection"
            no-close-on-backdrop
            size="lg"
            @ok="event => saveProcessSection(event, `object-section-team-modal`, `Команда и доступ сохранены`)"
            @hidden="clearSectionError"
        >
            <b-alert v-if="sectionError" show variant="danger">
                {{ sectionError }}
            </b-alert>

            <div v-if="processDraftEnabled" class="object-form-grid">
                <b-form-group label="Прораб" label-for="process-draft-staff">
                    <v-select
                        id="process-draft-staff"
                        label="full_name"
                        :value="users.find(user => Number(user.user_id) === Number(processDraftForm.main_staff_id))"
                        :options="users"
                        :clearable="true"
                        :disabled="sectionSaving"
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                        @input="setDraftMainStaff"
                    ></v-select>
                </b-form-group>

                <b-form-group label="Куратор" label-for="process-draft-curator">
                    <v-select
                        id="process-draft-curator"
                        label="full_name"
                        :value="users.find(user => Number(user.user_id) === Number(processDraftForm.curator_id))"
                        :options="users"
                        :clearable="true"
                        :disabled="sectionSaving"
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                        @input="setDraftCurator"
                    ></v-select>
                </b-form-group>

                <b-form-group label="Имеющие доступ" class="object-form-grid__wide">
                    <v-select
                        label="full_name"
                        :value="selectedDraftHasAccessUsers"
                        :options="availableUsersForDraftHasAccess"
                        :clearable="true"
                        :disabled="sectionSaving"
                        multiple
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                        @input="value => processDraftForm.has_access = value ? value.map(user => user.user_id) : []"
                    ></v-select>
                </b-form-group>

                <div class="object-form-grid__wide">
                    <label class="d-block">Доступ к галерее</label>
                    <MultipleParticipantsConfig
                        v-model="processDraftForm.gallery_access_config"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        participant-label="получателя доступа"
                        :show-object-roles="true"
                        :allowed-types="galleryObjectAccessTypes"
                        :object-role-options="galleryObjectRoleOptions"
                        :show-fallback-option="false"
                        :show-priority="false"
                        empty-text="Отдельный доступ к галерее объекта не настроен"
                        hint-text="Эти участники видят фотографии только этого объекта"
                    />
                </div>
            </div>
        </b-modal>

        <b-modal
            id="object-target-modal"
            :title="targetModalTitle"
            :ok-title="targetSubmitTitle"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="targetSaving || !targetFormValid"
            no-close-on-backdrop
            @ok="submitTargetForm"
            @hidden="clearTargetForm"
        >
            <b-alert v-if="targetFormError" show variant="danger">
                {{ targetFormError }}
            </b-alert>

            <b-form-group label="Объект">
                <b-form-input
                    :value="title"
                    size="sm"
                    disabled
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Работа" label-for="object-target-work-type">
                <v-select
                    id="object-target-work-type"
                    v-model="targetForm.workType"
                    :class="targetForm.workType ? 'target-form-select--valid' : 'target-form-select--invalid'"
                    :disabled="targetEditMode || targetSaving"
                    label="name"
                    :options="workTypes"
                    :clearable="!targetEditMode"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                    :filter="(options, search) => searchText(options, search, [`group_name`, `name`])"
                >
                    <template #option="{name, group_name}">
                        <span class="small font-weight-bold">Имя:</span> {{ name }}
                        <span v-if="group_name">
                            <span class="small font-weight-bold">Группа:</span>
                            {{ group_name }}
                        </span>
                    </template>
                </v-select>
                <div
                    v-if="!targetEditMode && $can('create', 'workTypes')"
                    class="d-flex justify-content-end mt-2"
                >
                    <b-button
                        variant="outline-success"
                        size="sm"
                        :disabled="targetSaving"
                        @click="showCreateWorkType"
                    >
                        <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                        Работы нет в списке
                    </b-button>
                </div>
            </b-form-group>

            <b-form-group label="Имя работы проектное" label-for="object-target-project-name">
                <b-form-input
                    id="object-target-project-name"
                    v-model.trim="targetForm.workNameProject"
                    placeholder="Ремонт швов сжатия на перроне"
                    size="sm"
                    :disabled="targetSaving"
                    :state="Boolean(targetForm.workNameProject)"
                ></b-form-input>
            </b-form-group>

            <b-form-group
                v-if="targetForm.workType"
                label-for="object-target-value"
            >
                <template #label>
                    Необходимо выполнить в единицах
                    <span class="text-success font-weight-bold text-capitalize">
                        {{ targetMeasurementText }}
                    </span>
                </template>
                <b-form-input
                    id="object-target-value"
                    v-model="targetForm.value"
                    size="sm"
                    :disabled="targetSaving"
                    :state="Boolean(targetForm.value)"
                    type="number"
                ></b-form-input>
            </b-form-group>
        </b-modal>

        <CreateWorkType
            :show="createWorkTypeVisible"
            @hidden="hideCreateWorkType"
            @created="onWorkTypeCreated"
        />

        <b-modal
            id="object-target-delete-modal"
            title="Удалить работу по договору"
            ok-title="Удалить"
            ok-variant="danger"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="targetDeleting"
            no-close-on-backdrop
            @ok="deleteTargetFromProgress"
            @hidden="clearTargetForm"
        >
            <b-alert v-if="targetFormError" show variant="danger">
                {{ targetFormError }}
            </b-alert>
            <p class="mb-0" v-if="editingTarget">
                Вы действительно хотите удалить работу по договору
                <span class="text-danger font-weight-bold">
                    {{ editingTarget.project_name || editingTarget.name }}
                </span>
                объекта
                <span class="text-danger font-weight-bold">{{ title }}</span>
                из системы?
            </p>
        </b-modal>

        <b-modal
            id="object-material-target-modal"
            :title="materialTargetModalTitle"
            :ok-title="materialTargetSubmitTitle"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="materialTargetSaving || !materialTargetFormValid"
            no-close-on-backdrop
            @ok="submitMaterialTargetForm"
            @hidden="clearMaterialTargetForm"
        >
            <b-alert v-if="materialTargetFormError" show variant="danger">
                {{ materialTargetFormError }}
            </b-alert>

            <div class="object-material-target-context">
                <div class="object-material-target-context__row">
                    <span>Объект</span>
                    <strong>{{ title }}</strong>
                </div>
                <div class="object-material-target-context__row">
                    <span>Работа</span>
                    <strong>{{ materialTargetWorkName(materialTargetWork) }}</strong>
                </div>
            </div>

            <div class="object-material-target-editor">
                <div v-if="materialTargetEditMode" class="object-material-target-editor__heading">
                    <span class="object-material-target-editor__icon" aria-hidden="true">
                        <i class="fas fa-cube"></i>
                    </span>
                    <div>
                        <span>Материал</span>
                        <strong>{{ materialTargetForm.material && materialTargetForm.material.name }}</strong>
                    </div>
                </div>

                <b-form-group
                    v-else
                    label="Материал"
                    label-for="object-material-target-material"
                >
                    <v-select
                        id="object-material-target-material"
                        v-model="materialTargetForm.material"
                        :class="materialTargetForm.material ? 'target-form-select--valid' : 'target-form-select--invalid'"
                        :disabled="materialTargetSaving"
                        label="name"
                        :options="materialTargetOptions"
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                        :filter="(options, search) => searchText(options, search, [`parent_group_name`, `name`, `measures`])"
                    >
                        <template #option="{name, parent_group_name, measures}">
                            <span class="small font-weight-bold">Имя:</span> {{ name }}
                            <span v-if="parent_group_name">
                                <span class="small font-weight-bold">Группа:</span>
                                {{ parent_group_name }}
                            </span>
                            <span v-if="measures">
                                <span class="small font-weight-bold">ЕИ:</span>
                                {{ measures }}
                            </span>
                        </template>
                    </v-select>
                </b-form-group>

                <b-form-group
                    class="mb-0"
                    label="Количество материала"
                    label-for="object-material-target-value"
                >
                    <b-input-group size="sm">
                        <b-form-input
                            id="object-material-target-value"
                            v-model="materialTargetForm.value"
                            :disabled="materialTargetSaving"
                            :state="isFilledNumberInput(materialTargetForm.value)"
                            type="number"
                        ></b-form-input>
                        <b-input-group-append v-if="selectedMaterialTargetUnit">
                            <b-input-group-text class="font-weight-bold">
                                {{ selectedMaterialTargetUnit }}
                            </b-input-group-text>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
                <div
                    v-if="materialTargetWorkScopeLabel"
                    class="object-material-target-scope"
                >
                    <i class="fas fa-info-circle" aria-hidden="true"></i>
                    <div>
                        <span>Количество указывается на весь объём работы</span>
                        <strong>{{ materialTargetWorkScopeLabel }}</strong>
                    </div>
                </div>
            </div>

            <b-form-group
                class="mb-0"
                label="Тревога сработает если отклонение превысит, %"
                label-for="object-material-target-limit"
            >
                <b-form-input
                    id="object-material-target-limit"
                    v-model="materialTargetForm.limit"
                    size="sm"
                    :disabled="materialTargetSaving"
                    :state="isFilledNumberInput(materialTargetForm.limit)"
                    type="number"
                ></b-form-input>
            </b-form-group>
        </b-modal>

        <b-modal
            id="object-material-target-delete-modal"
            title="Удалить объем материала"
            ok-title="Удалить"
            ok-variant="danger"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :ok-disabled="materialTargetDeleting"
            no-close-on-backdrop
            @ok="deleteMaterialTargetFromProgress"
            @hidden="clearMaterialTargetForm"
        >
            <b-alert v-if="materialTargetFormError" show variant="danger">
                {{ materialTargetFormError }}
            </b-alert>
            <p class="mb-0" v-if="editingMaterialTarget">
                Вы действительно хотите удалить объем материала
                <span class="text-danger font-weight-bold">{{ editingMaterialTarget.name }}</span>
                по виду работ
                <span class="text-danger font-weight-bold">{{ materialTargetWorkName(materialTargetWork) }}</span>
                строительного объекта
                <span class="text-danger font-weight-bold">{{ title }}</span>
                из системы?
            </p>
        </b-modal>
    </div>
</template>

<script>
import DatePicker from '@/components/DatePicker/DatePicker.vue';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';
import requestsApi from '@/store/Requests/api';
import api from '@/utils/api';
import reportsApi from '@/store/Reports/api';
import RequestShortItem from '@/views/Requests/RequestShortItem/RequestShortItem.vue';
import TaskShortItem from '@/views/Tasks/TaskShortItem/TaskShortItem.vue';
import ReportBadges from '@/views/Reports/ReportBadges.vue';
import ObjectActivityBlock from './components/ObjectActivityBlock.vue';
import ObjectTelegramGroups from './components/ObjectTelegramGroups.vue';
import ObjectTelegramSummary from './components/ObjectTelegramSummary.vue';
import ObjectGroupMembership from './components/ObjectGroupMembership.vue';
import ObjectGalleryBlock from './components/ObjectGalleryBlock.vue';
import ObjectRelatedAccessNotice from './components/ObjectRelatedAccessNotice.vue';
import EmployeeLink from '@/components/EmployeeLink.vue';
import {telegramGroupHref} from '@/utils/objectTelegramGroups';
import CreateWorkType from '@/components/CreateWorkType/CreateWorkType.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import searchText from '@/utils/searchText';
import {
	formatPhone,
	normalizePhoneForStorage,
	phoneValidationError as getPhoneValidationError
} from '@/utils/phone';
import { positionVueSelectDropdown } from '@/utils/vueSelectPositioning';
import {updateRecentWorkItem} from '@/utils/recentWork';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

function defaultGalleryAccessConfig() {
    return {participants: []};
}

function emptyForm() {
    return {
        name: ``,
        phone: ``,
        address: ``,
        country: `BY`
    };
}

function emptyProcessForm() {
    return {
        curator_id: null,
        main_staff_id: null,
        folder_path: ``,
        report_time: `09:00:00`,
        common_chat_id: ``,
        is_active: true,
        need_notification: true,
        contract_name: ``,
        contract_date: null,
        has_access: [],
        gallery_access_config: defaultGalleryAccessConfig()
    };
}

function emptyTargetForm() {
    return {
        workType: null,
        workNameProject: ``,
        value: null
    };
}

function emptyMaterialTargetForm() {
    return {
        material: null,
        value: null,
        limit: 0
    };
}

function clone(value) {
    return JSON.parse(JSON.stringify(value || {}));
}

export default {
    name: `ObjectCard`,
    components: {
        ObjectTelegramGroups,
        ObjectTelegramSummary,
        ObjectGroupMembership,
        DatePicker,
        MultipleParticipantsConfig,
        RequestShortItem,
        TaskShortItem,
        ReportBadges,
        ObjectActivityBlock,
        ObjectGalleryBlock,
        ObjectRelatedAccessNotice,
        EmployeeLink,
		CreateWorkType,
		PhoneInput
    },
    data() {
        return {
            telegramSummaryError: '',
            form: emptyForm(),
            processForm: emptyProcessForm(),
            objectDraftForm: emptyForm(),
            processDraftForm: emptyProcessForm(),
            processDraftEnabled: false,
            sectionSaving: false,
            sectionError: null,
            archiveCheck: null,
            archiveCheckError: '',
            archiveChecking: false,
            archiveCheckSequence: 0,
            processEnabled: false,
            editMode: false,
            activeTabIndex: 0,
            saving: false,
            deleting: false,
            commentSubmitting: false,
            errorMessage: null,
            relatedRequests: [],
            relatedRequestsTotal: 0,
            relatedRequestsLoading: false,
            relatedRequestsError: null,
            relatedRequestsAccessLimited: false,
            relatedTasks: [],
            relatedTasksLoading: false,
            relatedTasksError: null,
            relatedTasksAccessLimited: false,
            relatedReports: [],
            relatedReportsTotal: 0,
            relatedReportCounts: {active: 0, annulled: 0},
            relatedReportStatus: `all`,
            relatedReportsLoading: false,
            relatedReportsError: null,
            relatedReportsAccessLimited: false,
            workProgressError: null,
            consumptionError: null,
            progressCollapsed: false,
            workProgressView: `works`,
            workProgressFilter: `all`,
            workProgressSearch: ``,
            workMaterialsExpanded: {},
            workGroupsExpanded: {},
            targetStatusChanging: {},
            materialTargetStatusChanging: {},
            targetForm: emptyTargetForm(),
            targetEditMode: false,
            targetSaving: false,
            targetDeleting: false,
            targetFormError: null,
            editingTarget: null,
            createWorkTypeVisible: false,
            materialTargetForm: emptyMaterialTargetForm(),
            materialTargetEditMode: false,
            materialTargetSaving: false,
            materialTargetDeleting: false,
            materialTargetFormError: null,
            materialTargetWork: null,
            editingMaterialTarget: null,
            fuelConsumptionFields: [
                {key: `report_id`, label: `Отчёт`, sortable: true},
                {key: `object_name`, label: `Объект`, sortable: true},
                {key: `date_time_begin`, label: `Начало смены`, sortable: true},
                {key: `date_time_end`, label: `Конец смены`, sortable: true},
                {key: `fuel_name`, label: `Вид топлива`, sortable: true},
                {key: `value`, label: `Количество`, sortable: true},
                {key: `unit`, label: `ЕИ`, sortable: true},
                {key: `notes`, label: `Техника`, sortable: true}
            ],
            materialConsumptionFields: [
                {key: `report_id`, label: `Отчёт`, sortable: true},
                {key: `object_name`, label: `Объект`, sortable: true},
                {key: `date_time_begin`, label: `Начало смены`, sortable: true},
                {key: `date_time_end`, label: `Конец смены`, sortable: true},
                {key: `work_name`, label: `Наименование работы основное`, sortable: true},
                {key: `work_project_name`, label: `Наименование работы дополнительное`, sortable: true},
                {key: `work_value`, label: `Объём работ`, sortable: true},
                {key: `work_unit`, label: `ЕИ`, sortable: true},
                {key: `material_name`, label: `Материал`, sortable: true},
                {key: `material_value`, label: `Фактически потрачено`, sortable: true},
                {key: `material_unit`, label: `ЕИ`, sortable: true}
            ],
            relatedReportsFields: [
                {key: `shift_id`, label: `Отчёт`, sortable: true},
                {key: `date_time_begin`, label: `Смена`, sortable: true},
                {key: `date_time_send`, label: `Подан`, sortable: true},
                {key: `author_name`, label: `Автор`, sortable: true},
                {key: `media`, label: `Фото`, sortable: true},
                {key: `issues`, label: `Индикаторы`}
            ],
            countryOptions: [
                {value: `BY`, text: `Беларусь`},
                {value: `RU`, text: `Россия`}
            ],
            galleryObjectAccessTypes: [`specific_users`, `department`, `object_role`],
            galleryObjectRoleOptions: [
                {value: `main_staff`, text: `Прораб`},
                {value: `curator`, text: `Куратор`},
                {value: `has_access`, text: `Имеющий доступ`}
            ]
        };
    },
    computed: {
        isCreate() {
            return this.$route.name === `object-create`;
        },
        objectId() {
            return Number(this.$route.params.id);
        },
        card() {
            return this.$store.state.objectCards.selected;
        },
        activity() {
            return this.$store.state.objectCards.activity;
        },
        cardLoading() {
            return this.$store.state.objectCards.cardLoading;
        },
        activityLoading() {
            return this.$store.state.objectCards.activityLoading;
        },
        progressLoading() {
            return this.$store.state.objectCards.progressLoading;
        },
        consumptionLoading() {
            return this.$store.state.objectCards.consumptionLoading;
        },
        reportDownloading() {
            return this.$store.state.objectCards.reportDownloading;
        },
        workProgress() {
            return this.$store.state.objectCards.progress;
        },
        objectConsumption() {
            return this.$store.state.objectCards.consumption || {fuel: [], materials: []};
        },
        objectFuelRows() {
            return Array.isArray(this.objectConsumption.fuel) ? this.objectConsumption.fuel : [];
        },
        objectMaterialRows() {
            return Array.isArray(this.objectConsumption.materials) ? this.objectConsumption.materials : [];
        },
        mobileTabCards() {
            const activityItems = this.activity && Array.isArray(this.activity.items)
                ? this.activity.items
                : [];
            const activityTotal = this.activity && this.activity.total != null
                ? Number(this.activity.total) || 0
                : activityItems.length;
            const reportCountsTotal = (Number(this.relatedReportCounts.active) || 0) +
                (Number(this.relatedReportCounts.annulled) || 0);
            const reportsTotal = reportCountsTotal || Number(this.relatedReportsTotal) || 0;
            return [
                {
                    key: `history`,
                    index: 0,
                    label: `История`,
                    icon: `fas fa-history`,
                    count: activityTotal,
                    loading: this.activityLoading,
                    error: false
                },
                {
                    key: `requests`,
                    index: 1,
                    label: `Заявки`,
                    icon: `fas fa-clipboard-list`,
                    count: Number(this.relatedRequestsTotal) || 0,
                    loading: this.relatedRequestsLoading,
                    error: Boolean(this.relatedRequestsError)
                },
                {
                    key: `tasks`,
                    index: 2,
                    label: `Задачи`,
                    icon: `fas fa-bell`,
                    count: this.relatedTasks.length,
                    loading: this.relatedTasksLoading,
                    error: Boolean(this.relatedTasksError)
                },
                {
                    key: `fuel`,
                    index: 3,
                    label: `Топливо`,
                    icon: `fas fa-gas-pump`,
                    count: this.objectFuelRows.length,
                    loading: this.consumptionLoading,
                    error: Boolean(this.consumptionError)
                },
                {
                    key: `materials`,
                    index: 4,
                    label: `Материалы`,
                    icon: `fas fa-cubes`,
                    count: this.objectMaterialRows.length,
                    loading: this.consumptionLoading,
                    error: Boolean(this.consumptionError)
                },
                {
                    key: `reports`,
                    index: 5,
                    label: `Отчёты`,
                    icon: `fas fa-flag-checkered`,
                    count: reportsTotal,
                    annulledCount: Number(this.relatedReportCounts.annulled) || 0,
                    loading: this.relatedReportsLoading,
                    error: Boolean(this.relatedReportsError)
                }
            ];
        },
        users() {
            return this.$store.state.users || [];
        },
        departments() {
            return this.$store.state.departments || [];
        },
        isAds() {
            return this.$store.getters.isAds;
        },
        title() {
            if (this.isCreate) {
                return `Новый строительный объект`;
            }
            return this.form.name || (this.card ? `Объект #${this.card.building_id}` : `Карточка объекта`);
        },
        status() {
            if (!this.card) {
                return this.processEnabled ? (this.processForm.is_active ? `active` : `archive`) : `without_process`;
            }
            return this.card.status;
        },
        statusLabel() {
            if (this.card && this.card.status_label) {
                return this.card.status_label;
            }
            const map = {
                active: `В работе`,
                archive: `Архив`,
                without_process: `Без процесса`
            };
            return map[this.status] || this.status;
        },
        hasProcess() {
            return Boolean(this.card && this.card.process);
        },
        permissions() {
            return (this.card && this.card.permissions) || {};
        },
        objectAccessSettings() {
            return this.$store.state.objectCards && this.$store.state.objectCards.accessSettings
                ? this.$store.state.objectCards.accessSettings
                : {};
        },
        canCreateObject() {
            return this.objectAccessSettings.can_create === true ||
                this.objectAccessSettings.can_manage_access === true ||
                this.$can(`manage`, `all`);
        },
        canCreateProcess() {
            return this.canCreateObject;
        },
        canEditObject() {
            return this.isCreate ? this.canCreateObject : Boolean(this.permissions.can_update_object);
        },
        canEditProcess() {
            if (this.isCreate) {
                return this.canCreateProcess;
            }
            return Boolean(this.permissions.can_update_process || (!this.hasProcess && this.permissions.can_create_process));
        },
        canComment() {
            return Boolean(this.permissions.can_comment);
        },
        canReadGallery() {
            return Boolean(this.permissions.can_read_gallery);
        },
        canDeleteObject() {
            return !this.isCreate &&
                Boolean(this.permissions.can_delete_object);
        },
        canStartEdit() {
            return !this.isCreate && (this.canEditObject || this.canEditProcess);
        },
        canSave() {
            return this.editMode && (this.canEditObject || this.canEditProcess);
        },
        nameState() {
            return this.form.name ? null : false;
        },
		objectPhoneValidationError() {
			return getPhoneValidationError(this.form.phone);
		},
        staffUser() {
            return this.userById(this.processForm.main_staff_id);
        },
        curatorUser() {
            return this.userById(this.processForm.curator_id);
        },
        selectedHasAccessUsers() {
            return (this.processForm.has_access || [])
                .map(userId => this.users.find(user => Number(user.user_id) === Number(userId)))
                .filter(Boolean);
        },
        selectedHasAccessUsersWithTelegram() {
            return this.selectedHasAccessUsers.filter(user => this.userTelegramUrl(user));
        },
        selectedHasAccessUsersWithoutTelegram() {
            return this.selectedHasAccessUsers.filter(user => !this.userTelegramUrl(user));
        },
        selectedDraftHasAccessUsers() {
            return (this.processDraftForm.has_access || [])
                .map(userId => this.users.find(user => Number(user.user_id) === Number(userId)))
                .filter(Boolean);
        },
        availableUsersForDraftHasAccess() {
            return this.users.filter(user => {
                return Number(user.user_id) !== Number(this.processDraftForm.curator_id) &&
                    Number(user.user_id) !== Number(this.processDraftForm.main_staff_id);
            });
        },
        objectDraftNameState() {
            return this.objectDraftForm.name ? null : false;
        },
		objectDraftPhoneValidationError() {
			return getPhoneValidationError(this.objectDraftForm.phone);
		},
        canSaveObjectSection() {
			return this.canEditObject
				&& Boolean(this.objectDraftForm.name && this.objectDraftForm.name.trim())
				&& !this.objectDraftPhoneValidationError;
        },
        canSaveProcessSection() {
            return this.processDraftEnabled && (this.canEditProcess || (!this.hasProcess && this.canCreateProcess));
        },
        buildingProcessId() {
            return this.card && this.card.process ? Number(this.card.process.building_process_id) : null;
        },
        workTypes() {
            return Array.isArray(this.$store.state.workTypes)
                ? this.$store.state.workTypes.filter(work => work.is_active !== false)
                : [];
        },
        selectedTargetMeasurement() {
            if (!this.targetForm.workType) {
                return null;
            }
            return this.workTypes.find(workType => Number(workType.work_id) === Number(this.targetForm.workType.work_id))
                || this.targetForm.workType;
        },
        targetMeasurementText() {
            return this.selectedTargetMeasurement && this.selectedTargetMeasurement.unit_of_measurement
                ? this.selectedTargetMeasurement.unit_of_measurement
                : ``;
        },
        canCreateTarget() {
            return this.hasProcess && this.$can(`create`, `targets`);
        },
        canUpdateTarget() {
            return this.hasProcess && this.$can(`update`, `targets`);
        },
        canDeleteTarget() {
            return this.hasProcess && this.$can(`delete`, `targets`);
        },
        targetFormValid() {
            return Boolean(
                this.buildingProcessId &&
                this.targetForm.workType &&
                this.targetForm.workType.work_id &&
                this.targetForm.value
            );
        },
        targetModalTitle() {
            return this.targetEditMode ? `Изменить работу по договору` : `Добавить работу по договору`;
        },
        targetSubmitTitle() {
            return this.targetEditMode ? `Сохранить` : `Добавить`;
        },
        materialsDirectory() {
            return this.$store.state.materials && Array.isArray(this.$store.state.materials.directory)
                ? this.$store.state.materials.directory
                : [];
        },
        materialTargetOptions() {
            const usedIds = new Set(((this.materialTargetWork && this.materialTargetWork.materials) || [])
                .filter(material => !this.editingMaterialTarget ||
                    Number(material.material_id || material.id) !== Number(this.editingMaterialTarget.material_id || this.editingMaterialTarget.id))
                .map(material => Number(material.material_id || material.id)));
            return this.materialsDirectory.filter(material => {
                return material.is_active !== false && !usedIds.has(Number(material.id));
            });
        },
        selectedMaterialTargetUnit() {
            const material = this.materialTargetForm.material;
            return material && (material.measures || material.unit) ? (material.measures || material.unit) : ``;
        },
        materialTargetWorkScopeLabel() {
            const work = this.materialTargetWork;
            if (!work) return ``;
            const name = this.materialTargetWorkName(work);
            const hasPlan = work.plan_value !== null && work.plan_value !== undefined;
            const amount = hasPlan
                ? this.formatAmount(work.plan_value, work.unit)
                : `объём не указан`;
            return `${name} — ${amount}`;
        },
        canCreateMaterialTarget() {
            return this.hasProcess && this.$can(`create`, `materialTargets`);
        },
        canUpdateMaterialTarget() {
            return this.hasProcess && this.$can(`update`, `materialTargets`);
        },
        canDeleteMaterialTarget() {
            return this.hasProcess && this.$can(`delete`, `materialTargets`);
        },
        materialTargetFormValid() {
            return Boolean(
                this.materialTargetWork &&
                this.materialTargetWork.id &&
                this.materialTargetForm.material &&
                this.materialTargetForm.material.id &&
                this.isFilledNumberInput(this.materialTargetForm.value) &&
                this.isFilledNumberInput(this.materialTargetForm.limit)
            );
        },
        materialTargetModalTitle() {
            return this.materialTargetEditMode ? `Изменить объём материала` : `Добавить объём материала`;
        },
        materialTargetSubmitTitle() {
            return this.materialTargetEditMode ? `Сохранить` : `Добавить`;
        },
        primaryTeamMembers() {
            return [
                this.teamMember(`staff`, `Прораб`, this.staffUser),
                this.teamMember(`curator`, `Куратор`, this.curatorUser)
            ];
        },
        objectDetails() {
            return [
                {key: `country`, label: `Страна`, value: this.countryText(this.form.country)},
                {key: `object`, label: `ID объекта`, value: this.card ? `#${this.card.building_id}` : `-`},
                {key: `process`, label: `ID процесса`, value: this.buildingProcessId ? `#${this.buildingProcessId}` : `Нет`}
            ];
        },
        objectIssueSummary() {
            const cardSummary = this.card && this.card.issue_summary ? this.card.issue_summary : {};
            const progressSummary = this.workProgressSummary || {};
            return {
                work_overruns_count: Number(progressSummary.work_overruns_count || cardSummary.work_overruns_count) || 0,
                material_issues_count: Number(progressSummary.material_issues_count || cardSummary.material_issues_count) || 0,
                overdue_requests_count: Math.max(Number(cardSummary.overdue_requests_count) || 0, this.overdueRelatedRequestsCount),
                overdue_tasks_count: Math.max(Number(cardSummary.overdue_tasks_count) || 0, this.overdueRelatedTasksCount)
            };
        },
        objectLastReportBadge() {
            return this.lastReportBadge(this.card);
        },
        objectLastReportPath() {
            const latestShift = this.workProgress && this.workProgress.latest_shift;
            const shiftId = Number(this.card && this.card.latest_report_shift_id || latestShift && latestShift.shift_id);
            return Number.isSafeInteger(shiftId) && shiftId > 0 ? `/reports/${shiftId}` : null;
        },
        overdueRelatedRequestsCount() {
            return this.relatedRequests.filter(request => this.isRequestOverdue(request)).length;
        },
        overdueRelatedTasksCount() {
            return this.relatedTasks.filter(task => this.isTaskOverdue(task)).length;
        },
        objectIssueItems() {
            const summary = this.objectIssueSummary;
            const items = [];
            if (summary.work_overruns_count > 0) {
                items.push({
                    key: `work_overruns`,
                    variant: `danger`,
                    icon: `fas fa-chart-line`,
                    text: `Работы сверх договора: ${summary.work_overruns_count}`,
                    title: `Выполнено больше чем по договору: ${summary.work_overruns_count}`
                });
            }
            if (summary.material_issues_count > 0) {
                items.push({
                    key: `material_issues`,
                    variant: `danger`,
                    icon: `fas fa-exclamation-triangle`,
                    text: `Отклонения материалов: ${summary.material_issues_count}`,
                    title: `Материалы с отклонениями от допустимого порога: ${summary.material_issues_count}`
                });
            }
            if (summary.overdue_requests_count > 0) {
                items.push({
                    key: `overdue_requests`,
                    variant: `warning`,
                    icon: `fas fa-clock`,
                    text: `Просрочено заявок: ${summary.overdue_requests_count}`,
                    title: `Перейти к заявкам объекта`
                });
            }
            if (summary.overdue_tasks_count > 0) {
                items.push({
                    key: `overdue_tasks`,
                    variant: `warning`,
                    icon: `fas fa-clock`,
                    text: `Просрочено задач: ${summary.overdue_tasks_count}`,
                    title: `Перейти к задачам объекта`
                });
            }
            return items;
        },
        objectHealthItems() {
            const items = [];
            if (this.card && this.card.is_history_only && !this.hasProcess && !this.processEnabled) return items;
            if (!this.hasProcess && !this.processEnabled && !(this.card && this.card.is_history_only)) {
                items.push({
                    key: `without_process`,
                    variant: `warning`,
                    icon: `fas fa-layer-group`,
                    text: `Нет рабочего процесса`
                });
                return items;
            }
            if (!this.staffUser) {
                items.push({
                    key: `staff`,
                    variant: `warning`,
                    icon: `fas fa-hard-hat`,
                    text: `Прораб не назначен`
                });
            }
            if (!this.curatorUser) {
                items.push({
                    key: `curator`,
                    variant: `warning`,
                    icon: `fas fa-user-tie`,
                    text: `Куратор не назначен`
                });
            }
            return items.concat(this.objectIssueItems);
        },
        telegramChatUrl() {
            return telegramGroupHref(this.processForm.common_chat_id);
        },
        objectFacts() {
            return [
                {
                    key: `phone`,
                    label: `Телефон`,
                    icon: `fas fa-phone`,
                    value: this.$formatPhone(this.form.phone, `Не указан`)
                },
                {
                    key: `contract`,
                    label: `Договор`,
                    icon: `fas fa-file-contract`,
                    value: this.contractText
                },
                {
                    key: `telegram`,
                    label: `Telegram`,
                    icon: `fas fa-paper-plane`,
                    value: this.telegramChatUrl ? `Основная` : `Не указана`,
                    href: this.telegramChatUrl
                },
                {
                    key: `folder`,
                    label: `Рабочая папка`,
                    icon: `fas fa-folder-open`,
                    value: this.processForm.folder_path || `Не указана`,
                    action: this.processForm.folder_path ? `copy_folder` : null,
                    title: this.processForm.folder_path ? `Копировать путь` : null,
                    multiline: true
                }
            ];
        },
        processFacts() {
            return [
                {
                    key: `active`,
                    label: `Статус`,
                    icon: `fas fa-toggle-on`,
                    value: this.processForm.is_active ? `В работе` : `В архиве`
                },
                {
                    key: `notifications`,
                    label: `Напоминания`,
                    icon: this.processForm.need_notification ? `fas fa-bell` : `fas fa-bell-slash`,
                    value: this.processForm.need_notification ? `Включены` : `Отключены`
                },
                {
                    key: `report_time`,
                    label: `Время отчета`,
                    icon: `fas fa-clock`,
                    value: this.processForm.report_time ? this.processForm.report_time.slice(0, 5) : `Не указано`
                },
                {
                    key: `chat`,
                    label: `Telegram`,
                    icon: `fas fa-paper-plane`,
                    value: this.telegramChatUrl ? `Основная` : `Не указана`
                }
            ];
        },
        contractText() {
            const parts = [
                this.processForm.contract_name,
                this.formatDate(this.processForm.contract_date)
            ].filter(Boolean);
            return parts.length ? parts.join(` от `) : `Не указан`;
        },
        availableUsersForHasAccess() {
            return this.users.filter(user => {
                return Number(user.user_id) !== Number(this.processForm.curator_id) &&
                    Number(user.user_id) !== Number(this.processForm.main_staff_id);
            });
        },
        participantUsers() {
            return this.users
                .filter(user => user && user.user_id)
                .map(user => ({
                    value: user.user_id,
                    text: user.full_name || `${user.last_name || ``} ${user.first_name || ``}`.trim()
                }));
        },
        participantDepartments() {
            return this.departments
                .filter(department => department && department.id)
                .map(department => ({
                    value: department.id,
                    text: department.name
                }));
        },
        galleryAccessSummary() {
            const participants = this.processForm.gallery_access_config &&
                Array.isArray(this.processForm.gallery_access_config.participants)
                ? this.processForm.gallery_access_config.participants
                : [];
            if (!participants.length) {
                return `Отдельный доступ к галерее объекта не настроен`;
            }
            return `Правил доступа: ${participants.length}`;
        },
        requestSortBy() {
            return this.$store.state.requests && this.$store.state.requests.sortBy
                ? this.$store.state.requests.sortBy
                : `status`;
        },
        requestSortDesc() {
            return Boolean(this.$store.state.requests && this.$store.state.requests.sortDesc);
        },
        taskSortBy() {
            return this.$store.state.tasksManagement && this.$store.state.tasksManagement.sortBy
                ? this.$store.state.tasksManagement.sortBy
                : `endDate`;
        },
        taskSortDesc() {
            return Boolean(this.$store.state.tasksManagement && this.$store.state.tasksManagement.sortDesc);
        },
        sortedRelatedTasks() {
            return this.sortTasksLikeNative(this.relatedTasks);
        },
        relatedRequestsTotalText() {
            if (!this.buildingProcessId) {
                return `Нет рабочего процесса`;
            }
            const shown = this.relatedRequests.length;
            const total = Number(this.relatedRequestsTotal) || 0;
            if (this.relatedRequestsAccessLimited) {
                return total > shown
                    ? `Показано ${shown} из ${total} доступных вам`
                    : `Доступно вам: ${total}`;
            }
            return total > shown ? `Показано ${shown} из ${total}` : `Всего: ${total}`;
        },
        relatedTasksTotalText() {
            if (!this.buildingProcessId) {
                return `Нет рабочего процесса`;
            }
            if (this.relatedTasksAccessLimited) {
                return `Доступно вам: ${this.relatedTasks.length}`;
            }
            return `Всего: ${this.relatedTasks.length}`;
        },
        relatedReportsTotalText() {
            if (!this.buildingProcessId) {
                return `Нет рабочего процесса`;
            }
            const shown = this.relatedReports.length;
            const total = Number(this.relatedReportsTotal) || 0;
            if (this.relatedReportsAccessLimited) {
                return total > shown
                    ? `Показано ${shown} из ${total} доступных вам`
                    : `Доступно вам: ${total}`;
            }
            return total > shown ? `Показано ${shown} из ${total}` : `Всего: ${total}`;
        },
        relatedReportStatusOptions() {
            const counts = this.relatedReportCounts || {};
            return [
                {
                    value: `all`,
                    text: `Все`,
                    count: Number(counts.active || 0) + Number(counts.annulled || 0),
                    activeVariant: `primary`
                },
                {value: `active`, text: `Действующие`, count: Number(counts.active || 0), activeVariant: `success`},
                {value: `annulled`, text: `Аннулированные`, count: Number(counts.annulled || 0), activeVariant: `danger`}
            ];
        },
        workProgressWorks() {
            return this.workProgress && Array.isArray(this.workProgress.works)
                ? this.workProgress.works
                : [];
        },
        workProgressGroups() {
            const groups = new Map();
            this.workProgressWorks.forEach(work => {
                const groupName = this.workGroupPath(work) || `Без группы`;
                if (!groups.has(groupName)) {
                    groups.set(groupName, {
                        id: groupName,
                        name: groupName,
                        plan_value: 0,
                        completed_value: 0,
                        works_count: 0,
                        completed_works_count: 0,
                        in_progress_works_count: 0,
                        not_started_works_count: 0,
                        work_overruns_count: 0,
                        material_issues_count: 0,
                        units: new Set(),
                        work_names: [],
                        works: []
                    });
                }

                const group = groups.get(groupName);
                const plan = Number(work && work.plan_value) || 0;
                const completed = Number(work && work.completed_value) || 0;
                group.plan_value += plan;
                group.completed_value += completed;
                group.works_count += 1;
                group.material_issues_count += Number(work && work.material_issues_count) || 0;
                if (work && work.unit) {
                    group.units.add(work.unit);
                }
                if (work && work.name) {
                    group.work_names.push(work.name);
                }
                group.works.push(work);
                if (work && (work.status === `completed` || work.status === `overdone`)) {
                    group.completed_works_count += 1;
                } else if (work && work.status === `in_progress`) {
                    group.in_progress_works_count += 1;
                } else {
                    group.not_started_works_count += 1;
                }
                if (this.isWorkOverrun(work)) {
                    group.work_overruns_count += 1;
                }
            });

            return Array.from(groups.values())
                .map(group => {
                    const progress = this.workProgressPercentFromAmounts(group.completed_value, group.plan_value);
                    const status = this.workProgressStatusFromAmounts(progress, group.completed_value, group.plan_value);
                    return {
                        ...group,
                        unit: group.units.size === 1 ? Array.from(group.units)[0] : ``,
                        plan_value: this.roundProgressAmount(group.plan_value),
                        completed_value: this.roundProgressAmount(group.completed_value),
                        progress_percent: progress,
                        progress_percent_capped: Math.max(0, Math.min(progress, 100)),
                        status: status.status,
                        status_label: status.label,
                        works: group.works.slice().sort((a, b) => {
                            return String(a && a.name || ``).localeCompare(String(b && b.name || ``), `ru`);
                        })
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name, `ru`));
        },
        worksWithMaterialIssuesCount() {
            return this.workProgressWorks.filter(work => Number(work.material_issues_count) > 0).length;
        },
        worksWithProgressIssuesCount() {
            return this.workProgressWorks.filter(work => this.hasWorkProgressIssue(work)).length;
        },
        groupsWithProgressIssuesCount() {
            return this.workProgressGroups.filter(group => this.hasWorkProgressIssue(group)).length;
        },
        filteredWorkProgressWorks() {
            const query = String(this.workProgressSearch || ``).trim().toLowerCase();
            const tokens = query ? query.split(/\s+/).filter(Boolean) : [];
            return this.workProgressWorks.filter(work => {
                if (this.workProgressFilter === `completed` &&
                    work.status !== `completed` &&
                    work.status !== `overdone`) {
                    return false;
                }
                if (this.workProgressFilter === `in_progress` && work.status !== `in_progress`) {
                    return false;
                }
                if (this.workProgressFilter === `material_issues` && !this.hasWorkProgressIssue(work)) {
                    return false;
                }
                if (!tokens.length) {
                    return true;
                }
                const haystack = [
                    work.name,
                    work.group_name,
                    work.parent_group_name,
                    work.work_group_name,
                    work.work_group_parent_name,
                    work.project_name,
                    work.full_name,
                    work.unit,
                    ...((work.materials || []).map(material => material && material.name))
                ].filter(Boolean).join(` `).toLowerCase();
                return tokens.every(token => haystack.indexOf(token) !== -1);
            });
        },
        filteredWorkProgressGroups() {
            const query = String(this.workProgressSearch || ``).trim().toLowerCase();
            const tokens = query ? query.split(/\s+/).filter(Boolean) : [];
            return this.workProgressGroups.filter(group => {
                if (this.workProgressFilter === `completed` &&
                    group.status !== `completed` &&
                    group.status !== `overdone`) {
                    return false;
                }
                if (this.workProgressFilter === `in_progress` && group.status !== `in_progress`) {
                    return false;
                }
                if (this.workProgressFilter === `material_issues` && !this.hasWorkProgressIssue(group)) {
                    return false;
                }
                if (!tokens.length) {
                    return true;
                }
                const haystack = [
                    group.name,
                    group.unit,
                    ...group.work_names
                ].filter(Boolean).join(` `).toLowerCase();
                return tokens.every(token => haystack.indexOf(token) !== -1);
            });
        },
        workProgressGroupSummary() {
            return this.workProgressGroups.reduce((summary, group) => {
                summary.groups_count += 1;
                if (group.status === `completed` || group.status === `overdone`) {
                    summary.completed_groups_count += 1;
                } else if (group.status === `in_progress`) {
                    summary.in_progress_groups_count += 1;
                } else {
                    summary.not_started_groups_count += 1;
                }
                if (this.hasWorkProgressIssue(group)) {
                    summary.issue_groups_count += 1;
                }
                return summary;
            }, {
                groups_count: 0,
                completed_groups_count: 0,
                in_progress_groups_count: 0,
                not_started_groups_count: 0,
                issue_groups_count: 0
            });
        },
        workProgressSummary() {
            const summary = this.workProgress && this.workProgress.summary ? this.workProgress.summary : {};
            return Object.assign({
                works_count: 0,
                completed_works_count: 0,
                in_progress_works_count: 0,
                not_started_works_count: 0,
                materials_count: 0,
                material_issues_count: 0,
                work_overruns_count: 0
            }, summary);
        },
        workProgressUpdatedText() {
            return this.formatDateTime(this.workProgress && this.workProgress.generated_at);
        },
        latestShiftText() {
            const shift = this.workProgress && this.workProgress.latest_shift;
            if (!shift || !shift.shift_id) {
                return ``;
            }
            const begin = this.formatDateTime(shift.date_time_begin);
            return begin ? `последняя смена #${shift.shift_id}, начало ${begin}` : `последняя смена #${shift.shift_id}`;
        },
        workProgressHeaderText() {
            const parts = [];
            if (this.workProgressUpdatedText) {
                parts.push(`По состоянию на ${this.workProgressUpdatedText}`);
            }
            if (this.latestShiftText) {
                parts.push(this.latestShiftText);
            }
            return parts.join(` · `);
        },
        workProgressFilterOptions() {
            const isGroups = this.workProgressView === `groups`;
            const summary = isGroups ? this.workProgressGroupSummary : this.workProgressSummary;
            return [
                {
                    key: `all`,
                    variant: `primary`,
                    icon: `fas fa-tasks`,
                    text: `Все`,
                    count: isGroups ? summary.groups_count : summary.works_count
                },
                {
                    key: `completed`,
                    variant: `success`,
                    icon: `fas fa-check-circle`,
                    text: `Завершено`,
                    count: isGroups ? summary.completed_groups_count : summary.completed_works_count
                },
                {
                    key: `in_progress`,
                    variant: `info`,
                    icon: `fas fa-play-circle`,
                    text: `В работе`,
                    count: isGroups ? summary.in_progress_groups_count : summary.in_progress_works_count
                },
                {
                    key: `material_issues`,
                    variant: `danger`,
                    icon: `fas fa-exclamation-triangle`,
                    text: `Отклонения`,
                    count: isGroups ? this.groupsWithProgressIssuesCount : this.worksWithProgressIssuesCount
                }
            ];
        },
        workProgressSearchPlaceholder() {
            return this.workProgressView === `groups`
                ? `Поиск по группам и работам`
                : `Поиск по работам, группам и материалам`;
        }
    },
    watch: {
        objectId() {
            this.archiveCheckSequence++;
            this.archiveCheck = null;
            this.archiveCheckError = '';
            this.archiveChecking = false;
        },
        card: {
            handler(value) {
                if (value) {
                    this.fillFromCard(value);
                    if (value.name && value.building_id) {
                        updateRecentWorkItem(
                            this.$store.getters.currentUser,
                            'object',
                            value.building_id,
                            {
                                label: value.name,
                                contextLabel: 'Объект'
                            }
                        );
                    }
                }
            },
            immediate: true
        },
        'processForm.curator_id'(value) {
            if (value && this.processForm.has_access.some(userId => Number(userId) === Number(value))) {
                this.processForm.has_access = this.processForm.has_access.filter(userId => Number(userId) !== Number(value));
            }
        },
        'processForm.main_staff_id'(value) {
            if (value && this.processForm.has_access.some(userId => Number(userId) === Number(value))) {
                this.processForm.has_access = this.processForm.has_access.filter(userId => Number(userId) !== Number(value));
            }
        },
        '$route.fullPath'() {
            this.load().catch(err => {
                this.errorMessage = this.errorText(err, `Ошибка загрузки карточки объекта`);
            });
        }
    },
    methods: {
        openTelegramManagement() { const panel=this.$refs.teamPanel; if(panel){panel.scrollIntoView({behavior:'smooth',block:'start'}); panel.focus({preventScroll:true});} },
        updateTelegramState(data) {
            this.telegramSummaryError = '';
            this.$store.commit('objectCards/setTelegramState', {buildingId: this.objectId, data});
        },
        async refreshTelegramState() {
            if (this.$refs.telegramGroups) return this.$refs.telegramGroups.refresh();
            const id = this.objectId;
            try {
                const {data} = await api.get(`/api/objects/unified/${id}/telegram-groups`, {ignoreModal: true});
                if (id === this.objectId) this.updateTelegramState(data);
            } catch (error) {
                if (id === this.objectId) this.telegramSummaryError = 'Не удалось обновить Telegram-группы. Повторите проверку.';
            }
        },
        async load() {
            this.errorMessage = null;
            await Promise.all([
                this.$store.dispatch(`getUsers`, {full: true}),
                this.$store.dispatch(`getDepartments`)
            ]);
            if (this.isCreate) {
                this.$store.commit(`objectCards/setSelected`, null);
                this.$store.commit(`objectCards/setActivity`, null);
                this.form = emptyForm();
                this.processForm = emptyProcessForm();
                this.processEnabled = false;
                this.$store.commit(`objectCards/setProgress`, null);
                this.$store.commit(`objectCards/setConsumption`, null);
                this.editMode = true;
                return;
            }
            this.editMode = false;
            await this.$store.dispatch(`objectCards/openObject`, this.objectId);
            await Promise.all([
                this.loadRelatedLists(),
                this.loadWorkProgress(),
                this.loadObjectConsumption()
            ]);
        },
        fillFromCard(card) {
            this.form = {
                name: card.name || ``,
				phone: formatPhone(card.phone),
                address: card.address || ``,
                country: card.country || `BY`
            };

            if (card.process) {
                const process = card.process;
                this.processEnabled = true;
                this.processForm = Object.assign(emptyProcessForm(), {
                    curator_id: process.curator_id,
                    main_staff_id: process.main_staff_id,
                    folder_path: process.folder_path || ``,
                    report_time: process.report_time || `09:00:00`,
                    common_chat_id: process.common_chat_id || ``,
                    is_active: process.is_active !== false,
                    need_notification: process.need_notification !== false,
                    contract_name: process.contract_name || ``,
                    contract_date: process.contract_date || null,
                    has_access: Array.isArray(process.has_access) ? process.has_access.slice() : [],
                    gallery_access_config: clone(process.gallery_access_config || defaultGalleryAccessConfig())
                });
            } else {
                this.processEnabled = false;
                this.processForm = emptyProcessForm();
            }
        },
        validate() {
            if (!this.form.name || !this.form.name.trim()) {
                this.errorMessage = `Название объекта обязательно`;
                return false;
            }
			if (this.objectPhoneValidationError) {
				this.errorMessage = this.objectPhoneValidationError;
				return false;
			}
            if ((this.processEnabled || this.hasProcess) && !this.canEditProcess && !this.isCreate) {
                return true;
            }
            return true;
        },
        buildPayload() {
            const payload = {
                object: {
                    name: this.form.name,
					phone: normalizePhoneForStorage(this.form.phone),
                    address: this.form.address,
                    country: this.form.country || `BY`
                }
            };

            if ((this.processEnabled || this.hasProcess) && this.canEditProcess) {
                payload.process = Object.assign({}, this.processForm, {
                    enabled: true,
                    need_notification: this.processForm.is_active ? this.processForm.need_notification : false,
                    has_access: this.processForm.has_access || [],
                    gallery_access_config: this.processForm.gallery_access_config || defaultGalleryAccessConfig()
                });
            }

            if (!this.canEditObject && !this.isCreate) {
                delete payload.object;
            }

            return payload;
        },
        async save() {
            if (!this.validate()) {
                return;
            }

            const beforeProcess = this.card && this.card.process ? clone(this.card.process) : null;
            const payload = this.buildPayload();
            const wasCreate = this.isCreate;
            this.saving = true;
            this.errorMessage = null;
            try {
                if (!this.isCreate && !await this.confirmArchive(payload)) {
                    this.errorMessage = this.sectionError;
                    return;
                }
                let card;
                if (this.isCreate) {
                    card = await this.$store.dispatch(`objectCards/createObject`, payload);
                    await this.afterProcessSave(null, card);
                    await this.refreshLegacyObjects();
                    await this.$router.replace({
                        name: `object-card`,
                        params: {id: String(card.building_id)}
                    }).catch(() => {});
                } else {
                    card = await this.$store.dispatch(`objectCards/updateObject`, {
                        id: this.objectId,
                        payload
                    });
                    await this.afterProcessSave(beforeProcess, card);
                    await this.inviteUsersToTelegram(beforeProcess, payload, card);
                    await this.refreshLegacyObjects();
                }
                this.fillFromCard(card);
                await Promise.all([
                    this.loadRelatedLists(),
                    this.loadWorkProgress(),
                    this.loadObjectConsumption()
                ]);
                this.$bvToast.toast(`Карточка объекта сохранена`, {
                    title: `Объекты`,
                    variant: `success`,
                    solid: true
                });
                notifyInstallPromotionSuccess({
                    source: wasCreate ? 'object-created' : 'object-updated'
                });
                this.editMode = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения объекта`);
            } finally {
                this.saving = false;
            }
        },
        startEdit() {
            this.editMode = true;
        },
        cancelEdit() {
            if (this.card) {
                this.fillFromCard(this.card);
            }
            this.editMode = false;
            this.errorMessage = null;
        },
        currentProcessDraft() {
            return Object.assign(emptyProcessForm(), clone(this.processForm), {
                has_access: (this.processForm.has_access || []).slice(),
                gallery_access_config: clone(this.processForm.gallery_access_config || defaultGalleryAccessConfig())
            });
        },
        prepareProcessDraft() {
            this.archiveCheckSequence++;
            this.archiveCheck = null;
            this.archiveCheckError = '';
            this.archiveChecking = false;
            this.processDraftForm = this.currentProcessDraft();
            this.processDraftEnabled = this.hasProcess || this.processEnabled;
            this.sectionError = null;
        },
        openObjectEditModal() {
            this.objectDraftForm = Object.assign(emptyForm(), clone(this.form));
            this.sectionError = null;
            this.$bvModal.show(`object-section-object-modal`);
        },
        openProcessEditModal() {
            this.prepareProcessDraft();
            this.$bvModal.show(`object-section-process-modal`);
        },
        openContractEditModal() {
            this.prepareProcessDraft();
            this.$bvModal.show(`object-section-contract-modal`);
        },
        openTeamEditModal() {
            this.prepareProcessDraft();
            this.$bvModal.show(`object-section-team-modal`);
        },
        clearSectionError() {
            this.sectionError = null;
        },
        onDraftActiveChange(value) {
            this.sectionError = null;
            if (!value) {
                this.processDraftForm.need_notification = false;
                this.loadArchiveCheck();
            } else {
                this.archiveCheckSequence++;
                this.archiveChecking = false;
                this.archiveCheck = null;
                this.archiveCheckError = '';
            }
        },
        async loadArchiveCheck() {
            const id = this.objectId;
            const sequence = ++this.archiveCheckSequence;
            this.archiveChecking = true;
            this.archiveCheckError = '';
            this.archiveCheck = null;
            try {
                const response = await api.get('/api/objects/unified/' + id + '/archive-check');
                if (sequence !== this.archiveCheckSequence || id !== this.objectId) return null;
                this.archiveCheck = response.data;
                return response.data;
            } catch (err) {
                if (sequence === this.archiveCheckSequence && id === this.objectId) {
                    this.archiveCheckError = this.errorText(err, 'Не удалось проверить возможность архивирования');
                }
                return null;
            } finally {
                if (sequence === this.archiveCheckSequence) this.archiveChecking = false;
            }
        },
        archiveDate(value) {
            return value ? new Date(value + 'T12:00:00Z').toLocaleDateString('ru-RU', {
                day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
            }) : '';
        },
        async confirmArchive(payload) {
            if (!payload.process || payload.process.is_active !== false
                || (this.card && this.card.process && this.card.process.is_active === false)) return true;
            const id = this.objectId;
            const checked = await this.loadArchiveCheck();
            if (!checked || !checked.can_archive) {
                this.sectionError = this.archiveCheckError || 'Нельзя отправить объект в архив: не закрыты зарплатные периоды.';
                return false;
            }
            if (!checked.early_archive) return true;
            const accepted = await this.$bvModal.msgBoxConfirm(
                'Рекомендуем архивировать объект не раньше ' + this.archiveDate(checked.recommended_archive_date)
                + ': после последнего отчёта должен пройти полный календарный месяц для завершения табелей, отчётов и расчёта зарплаты.',
                {title: 'Раннее архивирование', okTitle: 'Архивировать раньше рекомендованной даты',
                    cancelTitle: 'Отмена', okVariant: 'warning', centered: true,
                    contentClass: 'argus-glass-dialog'}
            );
            return Boolean(accepted && id === this.objectId);
        },
        setDraftMainStaff(value) {
            this.processDraftForm.main_staff_id = value ? value.user_id : null;
            if (this.processDraftForm.main_staff_id) {
                this.processDraftForm.has_access = (this.processDraftForm.has_access || [])
                    .filter(userId => Number(userId) !== Number(this.processDraftForm.main_staff_id));
            }
        },
        setDraftCurator(value) {
            this.processDraftForm.curator_id = value ? value.user_id : null;
            if (this.processDraftForm.curator_id) {
                this.processDraftForm.has_access = (this.processDraftForm.has_access || [])
                    .filter(userId => Number(userId) !== Number(this.processDraftForm.curator_id));
            }
        },
        buildProcessDraftPayload() {
            return Object.assign({}, this.processDraftForm, {
                enabled: true,
                need_notification: this.processDraftForm.is_active ? this.processDraftForm.need_notification : false,
                has_access: this.processDraftForm.has_access || [],
                gallery_access_config: this.processDraftForm.gallery_access_config || defaultGalleryAccessConfig()
            });
        },
        async applyObjectUpdate(payload, successMessage) {
            const beforeProcess = this.card && this.card.process ? clone(this.card.process) : null;
            this.sectionSaving = true;
            this.sectionError = null;
            try {
                if (!await this.confirmArchive(payload)) return null;
                const card = await this.$store.dispatch(`objectCards/updateObject`, {
                    id: this.objectId,
                    payload
                });
                await this.afterProcessSave(beforeProcess, card);
                await this.inviteUsersToTelegram(beforeProcess, payload, card);
                await this.refreshLegacyObjects();
                this.fillFromCard(card);
                await Promise.all([
                    this.loadRelatedLists(),
                    this.loadWorkProgress(),
                    this.loadObjectConsumption()
                ]);
                this.$bvToast.toast(successMessage || `Карточка объекта сохранена`, {
                    title: `Объекты`,
                    variant: `success`,
                    solid: true
                });
                notifyInstallPromotionSuccess({source: 'object-updated'});
                return card;
            } catch (err) {
                if (err.response && err.response.data && err.response.data.code === 'OBJECT_ARCHIVE_PAYROLL_OPEN') {
                    this.archiveCheck = err.response.data.details;
                }
                this.sectionError = this.errorText(err, `Ошибка сохранения карточки объекта`);
                throw err;
            } finally {
                this.sectionSaving = false;
            }
        },
        async saveObjectSection(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.canSaveObjectSection) {
                return;
            }
            try {
                await this.applyObjectUpdate({
                    object: {
                        name: this.objectDraftForm.name,
						phone: normalizePhoneForStorage(this.objectDraftForm.phone),
                        address: this.objectDraftForm.address,
                        country: this.objectDraftForm.country || `BY`
                    }
                }, `Основные данные сохранены`);
                this.$bvModal.hide(`object-section-object-modal`);
            } catch (err) {
                return null;
            }
        },
        async saveProcessSection(event, modalId, successMessage) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.canSaveProcessSection) {
                return;
            }
            try {
                const saved = await this.applyObjectUpdate({
                    process: this.buildProcessDraftPayload()
                }, successMessage);
                if (!saved) return;
                this.$bvModal.hide(modalId);
            } catch (err) {
                return null;
            }
        },
        async confirmDeleteObject() {
            if (!this.canDeleteObject || this.deleting) {
                return;
            }
            const objectName = this.title;
            const firstConfirm = await this.$bvModal.msgBoxConfirm(
                `Объект "${objectName}" будет удален. Продолжить?`,
                {
                    title: `Удаление объекта`,
                    okTitle: `Продолжить`,
                    cancelTitle: `Отмена`,
                    okVariant: `danger`,
                    cancelVariant: `outline-secondary`,
                    centered: true
                }
            );
            if (!firstConfirm) {
                return;
            }
            const secondConfirm = await this.$bvModal.msgBoxConfirm(
                `Повторное подтверждение: удалить объект "${objectName}" без возможности отмены?`,
                {
                    title: `Подтвердите удаление`,
                    okTitle: `Удалить объект`,
                    cancelTitle: `Отмена`,
                    okVariant: `danger`,
                    cancelVariant: `outline-secondary`,
                    centered: true
                }
            );
            if (!secondConfirm) {
                return;
            }
            this.deleting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`objectCards/deleteObject`, this.objectId);
                notifyInstallPromotionSuccess({source: 'object-deleted'});
                await this.refreshLegacyObjects();
                this.$bvToast.toast(`Объект удален`, {
                    title: `Объекты`,
                    variant: `success`,
                    solid: true
                });
                await this.$router.replace({path: `/objects`}).catch(() => {});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка удаления объекта`);
            } finally {
                this.deleting = false;
            }
        },
        async afterProcessSave(beforeProcess, card) {
            const process = card && card.process ? card.process : null;
            if (!process) {
                return;
            }
            const wasActive = beforeProcess ? beforeProcess.is_active !== false : false;
            const isActive = process.is_active !== false;
            try {
                if (isActive && !wasActive) {
                    await this.$store.dispatch(`createReportManagement`, process.building_process_id);
                    await this.$store.dispatch(`createAlertManagement`, process.building_process_id);
                } else if (!isActive && wasActive) {
                    await this.$store.dispatch(`deleteActiveReportByProcess`, process.building_process_id);
                    await this.$store.dispatch(`deleteActiveAlertByProcess`, process.building_process_id);
                }
                if (beforeProcess) {
                    await this.$store.dispatch(`restartNotifications`);
                }
            } catch (err) {
                console.error(`Не удалось обновить регламентные действия объекта`, err);
            }
        },
        async inviteUsersToTelegram(beforeProcess, payload, card) {
            if (!beforeProcess || !payload || !payload.process) {
                return;
            }
            const process = payload.process;
            const chatId = beforeProcess.common_chat_id || (card.process && card.process.common_chat_id);
            if (!chatId) {
                return;
            }
            const participants = [];
            if (Number(beforeProcess.main_staff_id) !== Number(process.main_staff_id) && process.main_staff_id) {
                const user = this.users.find(item => Number(item.user_id) === Number(process.main_staff_id));
                if (user) {
                    participants.push(Object.assign({}, user, {isStaff: true}));
                }
            }
            if (Number(beforeProcess.curator_id) !== Number(process.curator_id) && process.curator_id) {
                const user = this.users.find(item => Number(item.user_id) === Number(process.curator_id));
                if (user) {
                    participants.push(Object.assign({}, user, {isCurator: true}));
                }
            }
            if (!participants.length) {
                return;
            }
            try {
                await this.$store.dispatch(`addUsersToTelegram`, {
                    id: chatId,
                    payload: {participants}
                });
            } catch (err) {
                this.$bvToast.toast(this.errorText(err, `Не удалось добавить участников в Telegram группу`), {
                    title: `Telegram`,
                    variant: `warning`,
                    solid: true
                });
            }
        },
        async refreshLegacyObjects() {
            await Promise.all([
                this.$store.dispatch(`getObjects`).catch(() => {}),
                this.$store.dispatch(`getObjectsInProcess`).catch(() => {})
            ]);
        },
        async loadRelatedLists() {
            if (!this.buildingProcessId) {
                this.relatedRequests = [];
                this.relatedRequestsTotal = 0;
                this.relatedRequestsAccessLimited = false;
                this.relatedTasks = [];
                this.relatedTasksAccessLimited = false;
                this.relatedReports = [];
                this.relatedReportsTotal = 0;
                this.relatedReportCounts = {active: 0, annulled: 0};
                this.relatedReportsAccessLimited = false;
                return;
            }
            await Promise.all([
                this.loadRelatedRequests(),
                this.loadRelatedTasks(),
                this.loadRelatedReports()
            ]);
        },
        async loadRelatedRequests() {
            if (!this.buildingProcessId) {
                return;
            }
            this.relatedRequestsLoading = true;
            this.relatedRequestsError = null;
            this.relatedRequestsAccessLimited = false;
            try {
                const payload = await requestsApi.getRequests({
                    tab: this.$store.getters.isAdmin ? `all_employees` : `all`,
                    page: 1,
                    per_page: 50,
                    sort_by: this.requestSortBy,
                    sort_desc: this.requestSortDesc,
                    building_process_id: this.buildingProcessId,
                    include_access_meta: true
                });
                this.relatedRequests = payload && Array.isArray(payload.items) ? payload.items : [];
                this.relatedRequestsTotal = payload && payload.total != null ? Number(payload.total) : this.relatedRequests.length;
                this.relatedRequestsAccessLimited = Boolean(payload && payload.access_limited === true);
            } catch (err) {
                this.relatedRequests = [];
                this.relatedRequestsTotal = 0;
                this.relatedRequestsError = this.errorText(err, `Ошибка загрузки заявок объекта`);
            } finally {
                this.relatedRequestsLoading = false;
            }
        },
        async loadRelatedTasks() {
            if (!this.buildingProcessId) {
                return;
            }
            this.relatedTasksLoading = true;
            this.relatedTasksError = null;
            this.relatedTasksAccessLimited = false;
            try {
                const payload = await this.$store.dispatch(`getTasks`, {
                    building_process_id: this.buildingProcessId,
                    include_access_meta: `true`
                });
                const tasks = payload && Array.isArray(payload.items) ? payload.items : payload;
                this.relatedTasks = (Array.isArray(tasks) ? tasks : [])
                    .filter(task => Number(task.building_process) === Number(this.buildingProcessId));
                this.relatedTasksAccessLimited = Boolean(payload && payload.access_limited === true);
                if (!this.$store.state.objectsInProcess.length) {
                    this.$store.dispatch(`getObjectsInProcess`).catch(() => {});
                }
            } catch (err) {
                this.relatedTasks = [];
                this.relatedTasksError = this.errorText(err, `Ошибка загрузки задач объекта`);
            } finally {
                this.relatedTasksLoading = false;
            }
        },
        async loadRelatedReports() {
            if (!this.buildingProcessId) {
                return;
            }
            this.relatedReportsLoading = true;
            this.relatedReportsError = null;
            this.relatedReportsAccessLimited = false;
            try {
                const payload = await reportsApi.getReports({
                    page: 1,
                    per_page: 50,
                    sort_by: `date_time_begin`,
                    sort_desc: `true`,
                    building_process_id: this.buildingProcessId,
                    report_status: this.relatedReportStatus,
                    include_access_meta: `true`
                });
                this.relatedReports = payload && Array.isArray(payload.items) ? payload.items : [];
                this.relatedReportsTotal = payload && payload.total != null ? Number(payload.total) : this.relatedReports.length;
                this.relatedReportCounts = payload && payload.counts
                    ? {
                        active: Number(payload.counts.active) || 0,
                        annulled: Number(payload.counts.annulled) || 0
                    }
                    : {
                        active: this.relatedReports.filter(report => !report.is_annulled).length,
                        annulled: this.relatedReports.filter(report => report.is_annulled).length
                    };
                this.relatedReportsAccessLimited = Boolean(payload && payload.access_limited === true);
            } catch (err) {
                this.relatedReports = [];
                this.relatedReportsTotal = 0;
                this.relatedReportCounts = {active: 0, annulled: 0};
                this.relatedReportsError = this.errorText(err, `Ошибка загрузки отчётов объекта`);
            } finally {
                this.relatedReportsLoading = false;
            }
        },
        async loadWorkProgress() {
            this.workProgressError = null;
            this.workMaterialsExpanded = {};
            this.workGroupsExpanded = {};
            if (!this.buildingProcessId) {
                this.$store.commit(`objectCards/setProgress`, null);
                return;
            }
            try {
                await this.$store.dispatch(`objectCards/fetchProgress`, this.objectId);
            } catch (err) {
                this.$store.commit(`objectCards/setProgress`, null);
                this.workProgressError = this.errorText(err, `Ошибка загрузки прогресса работ`);
            }
        },
        async loadObjectConsumption() {
            this.consumptionError = null;
            if (!this.buildingProcessId) {
                this.$store.commit(`objectCards/setConsumption`, null);
                return;
            }
            try {
                await this.$store.dispatch(`objectCards/fetchConsumption`, this.objectId);
            } catch (err) {
                this.$store.commit(`objectCards/setConsumption`, null);
                this.consumptionError = this.errorText(err, `Ошибка загрузки расхода по объекту`);
            }
        },
        refreshWorkProgress() {
            this.loadWorkProgress();
        },
        progressDisplayText(value) {
            const text = String(value || ``).trim();
            return text && text.toLowerCase() !== `null` && text.toLowerCase() !== `undefined`
                ? text
                : ``;
        },
        workGroupPath(work) {
            const names = [
                work && (work.parent_group_name || work.work_group_parent_name),
                work && (work.group_name || work.work_group_name)
            ]
                .map(value => this.progressDisplayText(value))
                .filter(Boolean)
                .filter((value, index, list) => list.indexOf(value) === index);

            return names.join(` / `);
        },
        workHeaderTitle(work) {
            return [
                work && work.name,
                work && work.project_name,
                this.workGroupPath(work)
            ]
                .map(value => this.progressDisplayText(value))
                .filter(Boolean)
                .join(` · `);
        },
        searchText,
        positionModalSelectDropdown: positionVueSelectDropdown,
        async ensureWorkTypes() {
            if (this.workTypes.length) {
                return;
            }
            await this.$store.dispatch(`getWorkTypes`);
        },
        targetWorkOption(work) {
            if (!work) {
                return null;
            }
            return this.workTypes.find(workType => Number(workType.work_id) === Number(work.work_id)) || {
                work_id: work.work_id,
                name: work.name,
                group_name: work.group_name,
                unit_of_measurement: work.unit
            };
        },
        clearTargetForm() {
            this.targetForm = emptyTargetForm();
            this.targetEditMode = false;
            this.targetSaving = false;
            this.targetDeleting = false;
            this.targetFormError = null;
            this.editingTarget = null;
        },
        showCreateWorkType() {
            this.createWorkTypeVisible = true;
        },
        hideCreateWorkType() {
            this.createWorkTypeVisible = false;
        },
        onWorkTypeCreated(workType) {
            if (workType && workType.work_id) {
                this.targetForm.workType = workType;
            }
            this.hideCreateWorkType();
            this.$bvToast.toast('Новый вид работ создан и выбран', {
                title: 'Работы по договору',
                variant: 'success',
                solid: true
            });
        },
        async openCreateTargetModal() {
            this.clearTargetForm();
            try {
                await this.ensureWorkTypes();
                this.$bvModal.show(`object-target-modal`);
            } catch (err) {
                this.$bvToast.toast(this.errorText(err, `Ошибка загрузки видов работ`), {
                    title: `Прогресс работ`,
                    variant: `danger`,
                    solid: true
                });
            }
        },
        async openEditTargetModal(work) {
            this.clearTargetForm();
            this.targetEditMode = true;
            this.editingTarget = work;
            try {
                await this.ensureWorkTypes();
                this.targetForm = {
                    workType: this.targetWorkOption(work),
                    workNameProject: work && work.project_name ? work.project_name : ``,
                    value: work && work.plan_value !== null && work.plan_value !== undefined
                        ? String(work.plan_value)
                        : null
                };
                this.$bvModal.show(`object-target-modal`);
            } catch (err) {
                this.clearTargetForm();
                this.$bvToast.toast(this.errorText(err, `Ошибка загрузки видов работ`), {
                    title: `Прогресс работ`,
                    variant: `danger`,
                    solid: true
                });
            }
        },
        openDeleteTargetModal(work) {
            this.clearTargetForm();
            this.editingTarget = work;
            this.$bvModal.show(`object-target-delete-modal`);
        },
        buildTargetPayload() {
            const workType = this.targetForm.workType || {};
            const target = this.editingTarget || {};
            return {
                building_process_id: this.buildingProcessId,
                building_name: this.title,
                work_id: workType.work_id || target.work_id,
                work_name_project: this.targetForm.workNameProject || ``,
                old_work_name_project: target.project_name || ``,
                value: this.targetForm.value,
                old_value: target.plan_value,
                work_name: target.name || workType.name
            };
        },
        async submitTargetForm(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.targetFormValid) {
                return;
            }
            this.targetSaving = true;
            this.targetFormError = null;
            try {
                const isEdit = this.targetEditMode;
                const action = this.targetEditMode
                    ? `objectCards/updateTarget`
                    : `objectCards/createTarget`;
                await this.$store.dispatch(action, this.buildTargetPayload());
                notifyInstallPromotionSuccess({
                    source: isEdit ? 'object-target-updated' : 'object-target-created'
                });
                this.$bvModal.hide(`object-target-modal`);
                await this.loadWorkProgress();
                this.$bvToast.toast(
                    isEdit ? `Работа по договору изменена` : `Работа по договору добавлена`,
                    {
                        title: `Прогресс работ`,
                        variant: `success`,
                        solid: true
                    }
                );
            } catch (err) {
                this.targetFormError = this.errorText(err, `Ошибка сохранения работы по договору`);
            } finally {
                this.targetSaving = false;
            }
        },
        async deleteTargetFromProgress(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.editingTarget || !this.buildingProcessId) {
                return;
            }
            this.targetDeleting = true;
            this.targetFormError = null;
            try {
                await this.$store.dispatch(`objectCards/deleteTarget`, {
                    building_process_id: this.buildingProcessId,
                    work_id: this.editingTarget.work_id
                });
                notifyInstallPromotionSuccess({source: 'object-target-deleted'});
                this.$bvModal.hide(`object-target-delete-modal`);
                await this.loadWorkProgress();
                this.$bvToast.toast(`Работа по договору удалена`, {
                    title: `Прогресс работ`,
                    variant: `success`,
                    solid: true
                });
            } catch (err) {
                this.targetFormError = this.errorText(err, `Ошибка удаления работы по договору`);
            } finally {
                this.targetDeleting = false;
            }
        },
        isTargetStatusChanging(work) {
            return Boolean(work && this.targetStatusChanging[String(work.id)]);
        },
        async changeTargetStatus(work) {
            if (!work || !work.id || this.isTargetStatusChanging(work)) return;
            const isActive = work.is_active === false;
            this.$set(this.targetStatusChanging, String(work.id), true);
            try {
                await this.$store.dispatch(`objectCards/setTargetStatus`, {id: work.id, isActive});
                notifyInstallPromotionSuccess({source: 'object-target-status-changed'});
                await this.loadWorkProgress();
                this.$bvToast.toast(
                    isActive ? `Работа сделана действующей` : `Работа отключена`,
                    {title: `Прогресс работ`, variant: `success`, solid: true}
                );
            } catch (err) {
                this.$bvToast.toast(this.errorText(err, `Не удалось изменить состояние работы`), {
                    title: `Прогресс работ`, variant: `danger`, solid: true
                });
            } finally {
                this.$delete(this.targetStatusChanging, String(work.id));
            }
        },
        isFilledNumberInput(value) {
            return value !== null && value !== undefined && value !== ``;
        },
        async ensureMaterialsDirectory() {
            if (this.materialsDirectory.length) {
                return;
            }
            await this.$store.dispatch(`getDirectory`);
        },
        materialTargetWorkName(work) {
            if (!work) {
                return ``;
            }
            return work.project_name ? `${work.project_name} (${work.name})` : work.name;
        },
        materialTargetOption(material) {
            if (!material) {
                return null;
            }
            const materialId = material.material_id || material.id;
            return this.materialsDirectory.find(item => Number(item.id) === Number(materialId)) || {
                id: materialId,
                name: material.name,
                measures: material.unit,
                parent_group_name: material.group_name
            };
        },
        clearMaterialTargetForm() {
            this.materialTargetForm = emptyMaterialTargetForm();
            this.materialTargetEditMode = false;
            this.materialTargetSaving = false;
            this.materialTargetDeleting = false;
            this.materialTargetFormError = null;
            this.materialTargetWork = null;
            this.editingMaterialTarget = null;
        },
        async openCreateMaterialTargetModal(work) {
            this.clearMaterialTargetForm();
            this.materialTargetWork = work;
            try {
                await this.ensureMaterialsDirectory();
                this.$bvModal.show(`object-material-target-modal`);
            } catch (err) {
                this.clearMaterialTargetForm();
                this.$bvToast.toast(this.errorText(err, `Ошибка загрузки материалов`), {
                    title: `Прогресс работ`,
                    variant: `danger`,
                    solid: true
                });
            }
        },
        async openEditMaterialTargetModal(work, material) {
            this.clearMaterialTargetForm();
            this.materialTargetEditMode = true;
            this.materialTargetWork = work;
            this.editingMaterialTarget = material;
            try {
                await this.ensureMaterialsDirectory();
                this.materialTargetForm = {
                    material: this.materialTargetOption(material),
                    value: material && material.plan_value !== null && material.plan_value !== undefined
                        ? String(material.plan_value)
                        : null,
                    limit: material && material.limit_percent !== null && material.limit_percent !== undefined
                        ? String(material.limit_percent)
                        : 0
                };
                this.$bvModal.show(`object-material-target-modal`);
            } catch (err) {
                this.clearMaterialTargetForm();
                this.$bvToast.toast(this.errorText(err, `Ошибка загрузки материалов`), {
                    title: `Прогресс работ`,
                    variant: `danger`,
                    solid: true
                });
            }
        },
        openDeleteMaterialTargetModal(work, material) {
            this.clearMaterialTargetForm();
            this.materialTargetWork = work;
            this.editingMaterialTarget = material;
            this.$bvModal.show(`object-material-target-delete-modal`);
        },
        buildMaterialTargetPayload() {
            return {
                targetWork: this.materialTargetWork && this.materialTargetWork.id,
                material: this.materialTargetForm.material && this.materialTargetForm.material.id,
                value: Number(this.materialTargetForm.value),
                limit: Number(this.materialTargetForm.limit)
            };
        },
        async submitMaterialTargetForm(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.materialTargetFormValid) {
                return;
            }
            this.materialTargetSaving = true;
            this.materialTargetFormError = null;
            try {
                const isEdit = this.materialTargetEditMode;
                if (isEdit) {
                    await this.$store.dispatch(`objectCards/updateMaterialTarget`, {
                        id: this.editingMaterialTarget.target_material_id,
                        payload: this.buildMaterialTargetPayload()
                    });
                } else {
                    await this.$store.dispatch(`objectCards/createMaterialTarget`, this.buildMaterialTargetPayload());
                }
                notifyInstallPromotionSuccess({
                    source: isEdit ? 'object-material-target-updated' : 'object-material-target-created'
                });
                this.$bvModal.hide(`object-material-target-modal`);
                await this.loadWorkProgress();
                this.$bvToast.toast(
                    isEdit ? `Объем материала изменен` : `Объем материала добавлен`,
                    {
                        title: `Прогресс работ`,
                        variant: `success`,
                        solid: true
                    }
                );
            } catch (err) {
                this.materialTargetFormError = this.errorText(err, `Ошибка сохранения объема материала`);
            } finally {
                this.materialTargetSaving = false;
            }
        },
        async deleteMaterialTargetFromProgress(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.editingMaterialTarget || !this.editingMaterialTarget.target_material_id) {
                return;
            }
            this.materialTargetDeleting = true;
            this.materialTargetFormError = null;
            try {
                await this.$store.dispatch(
                    `objectCards/deleteMaterialTarget`,
                    this.editingMaterialTarget.target_material_id
                );
                notifyInstallPromotionSuccess({source: 'object-material-target-deleted'});
                this.$bvModal.hide(`object-material-target-delete-modal`);
                await this.loadWorkProgress();
                this.$bvToast.toast(`Объем материала удален`, {
                    title: `Прогресс работ`,
                    variant: `success`,
                    solid: true
                });
            } catch (err) {
                this.materialTargetFormError = this.errorText(err, `Ошибка удаления объема материала`);
            } finally {
                this.materialTargetDeleting = false;
            }
        },
        isMaterialTargetStatusChanging(material) {
            const id = material && material.target_material_id;
            return Boolean(id && this.materialTargetStatusChanging[String(id)]);
        },
        async changeMaterialTargetStatus(material) {
            const id = material && material.target_material_id;
            if (!id || this.isMaterialTargetStatusChanging(material)) return;
            const isActive = material.is_active === false;
            this.$set(this.materialTargetStatusChanging, String(id), true);
            try {
                await this.$store.dispatch(`objectCards/setMaterialTargetStatus`, {id, isActive});
                notifyInstallPromotionSuccess({source: 'object-material-target-status-changed'});
                await this.loadWorkProgress();
                this.$bvToast.toast(
                    isActive ? `Норма материала сделана действующей` : `Норма материала отключена`,
                    {title: `Прогресс работ`, variant: `success`, solid: true}
                );
            } catch (err) {
                this.$bvToast.toast(this.errorText(err, `Не удалось изменить состояние нормы материала`), {
                    title: `Прогресс работ`, variant: `danger`, solid: true
                });
            } finally {
                this.$delete(this.materialTargetStatusChanging, String(id));
            }
        },
        sortTasksLikeNative(tasks) {
            const sortBy = this.taskSortBy;
            const sortDesc = this.taskSortDesc;
            const sortField = sortBy === `endDate` ? `sortDate` : sortBy;
            const list = (tasks || []).map(task => Object.assign({}, task, {
                sortDate: task.endDate || task.cronDateFormat
            }));
            const sortByField = items => this.$_.sortBy(items, sortField);
            const empty = list.filter(item => !item[sortField]);
            const hasValue = list.filter(item => item[sortField]);
            const activeSorted = sortByField(hasValue.filter(item => item.status === 0));
            const closedSorted = sortByField(hasValue.filter(item => item.status === 1));
            return sortDesc
                ? activeSorted.reverse().concat(empty, closedSorted)
                : activeSorted.concat(empty, closedSorted.reverse());
        },
        async addComment({text, files, draft, done}) {
            this.commentSubmitting = true;
            try {
                await this.$store.dispatch(`objectCards/addComment`, {
                    id: this.card.building_id,
                    text,
                    files,
                    draft
                });
                if (done) done();
                notifyInstallPromotionSuccess({source: 'object-comment-added'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка добавления комментария`);
                if (done) done(err);
            } finally {
                this.commentSubmitting = false;
            }
        },
        async editComment({id, text}) {
            try {
                await this.$store.dispatch(`objectCards/updateComment`, {
                    id: this.card.building_id,
                    commentId: id,
                    text
                });
                notifyInstallPromotionSuccess({source: 'object-comment-edited'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка редактирования комментария`);
            }
        },
        async removeComment(commentId) {
            try {
                await this.$store.dispatch(`objectCards/deleteComment`, {
                    id: this.card.building_id,
                    commentId
                });
                notifyInstallPromotionSuccess({source: 'object-comment-deleted'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка удаления комментария`);
            }
        },
        async removeFile({commentId, fileId}) {
            try {
                await this.$store.dispatch(`objectCards/deleteCommentFile`, {
                    id: this.card.building_id,
                    commentId,
                    fileId
                });
                notifyInstallPromotionSuccess({source: 'object-comment-file-deleted'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка удаления файла`);
            }
        },
        async loadMoreActivity() {
            if (!this.card || this.activityLoading || !this.activity.has_more) {
                return;
            }
            try {
                await this.$store.dispatch(`objectCards/fetchActivity`, {
                    id: this.card.building_id,
                    append: true
                });
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки истории объекта`);
            }
        },
        onActiveChange(value) {
            if (!value) {
                this.processForm.need_notification = false;
            }
        },
        goBack() {
            const query = !this.isCreate && this.card && this.card.building_id
                ? {highlight: String(this.card.building_id)}
                : {};
            this.$router.push({path: `/objects`, query}).catch(() => {});
        },
        folderFormatter(value) {
            let result = String(value || ``).replace(/\\/g, `/`);
            if (result && result.slice(-1) !== `/`) {
                result += `/`;
            }
            return result;
        },
        statusVariant(status) {
            const map = {
                active: `success`,
                archive: `secondary`,
                without_process: `warning`
            };
            return map[status] || `light`;
        },
        isRequestOverdue(request) {
            if (!request || !request.deadline) {
                return false;
            }
            if ([`completed`, `rejected`, `cancelled`].indexOf(request.status) !== -1) {
                return false;
            }
            const deadline = new Date(request.deadline);
            return Number.isFinite(deadline.getTime()) && deadline.getTime() < Date.now();
        },
        isTaskOverdue(task) {
            if (!task || Number(task.status) === 1) {
                return false;
            }
            const endDateValue = task.endDate || task.cronDateFormat;
            if (!endDateValue) {
                return false;
            }
            const endDate = new Date(endDateValue);
            return Number.isFinite(endDate.getTime()) && endDate.getTime() < Date.now();
        },
        countryText(value) {
            const map = {
                BY: `Беларусь`,
                RU: `Россия`
            };
            return map[value] || value || `Не указана`;
        },
        userById(userId) {
            if (!userId) {
                return null;
            }
            return this.users.find(user => Number(user.user_id) === Number(userId)) || null;
        },
        userDisplayName(user) {
            if (!user) {
                return `Не назначен`;
            }
            return user.full_name || `${user.last_name || ``} ${user.first_name || ``}`.trim() || `Пользователь #${user.user_id}`;
        },
        userInitials(user) {
            const name = this.userDisplayName(user);
            return name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0).toUpperCase())
                .join(``) || `-`;
        },
        userTelegramUrl(user) {
            return user && user.phone ? this.$telegramPhoneHref(user.phone) : null;
        },
        teamMember(key, role, user) {
            return {
                key,
                role,
                user,
                userId: user && user.user_id ? user.user_id : null,
                name: this.userDisplayName(user),
                initials: user ? this.userInitials(user) : `-`,
                phone: user && user.phone ? user.phone : ``,
                telegramUrl: this.userTelegramUrl(user)
            };
        },
        formatDate(value) {
            if (!value) {
                return ``;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return value;
            }
            return date.toLocaleDateString(`ru-RU`, {
                day: `2-digit`,
                month: `2-digit`,
                year: `numeric`
            });
        },
        handleFactAction(fact) {
            if (fact && fact.action === `copy_folder`) {
                this.copyFolderPath();
            }
        },
        async copyToClipboard(text, successMessage) {
            const value = String(text || ``);
            if (!value) {
                return;
            }
            try {
                if (typeof navigator !== `undefined` && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(value);
                } else {
                    const textarea = document.createElement(`textarea`);
                    textarea.value = value;
                    textarea.setAttribute(`readonly`, `readonly`);
                    textarea.style.position = `fixed`;
                    textarea.style.opacity = `0`;
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand(`copy`);
                    document.body.removeChild(textarea);
                }
                this.$bvToast.toast(successMessage || `Скопировано`, {
                    title: `Карточка объекта`,
                    variant: `success`,
                    solid: true
                });
            } catch (err) {
                this.$bvToast.toast(`Не удалось скопировать. Выделите путь вручную.`, {
                    title: `Карточка объекта`,
                    variant: `warning`,
                    solid: true
                });
            }
        },
        copyFolderPath() {
            this.copyToClipboard(this.processForm.folder_path, `Путь рабочей папки скопирован`);
        },
        async focusObjectSection(refName) {
            await this.$nextTick();
            const section = this.$refs[refName];
            const element = section && (section.$el || section);
            if (!element) return;
            element.focus({preventScroll: true});
            element.scrollIntoView({block: `start`, behavior: `auto`});
        },
        openHealthItem(key) {
            if (key === `work_overruns` || key === `material_issues`) {
                this.progressCollapsed = false;
                this.setWorkProgressView(`works`);
                this.workProgressSearch = ``;
                this.setWorkProgressFilter(`material_issues`);
                return this.focusObjectSection(`progressPanel`);
            }
            if (key === `overdue_requests` || key === `overdue_tasks`) {
                this.activeTabIndex = key === `overdue_requests` ? 1 : 2;
                return this.focusObjectSection(`relatedTabs`);
            }
            if (key === `staff` || key === `curator`) {
                if (this.canEditProcess) return this.openTeamEditModal();
                return this.focusObjectSection(`teamPanel`);
            }
            if (key === `without_process`) {
                if (this.canEditProcess || this.canCreateProcess) return this.openProcessEditModal();
                return this.focusObjectSection(`processPanel`);
            }
        },
        openRequests() {
            if (this.buildingProcessId) {
                this.$store.commit(`requests_setBuildingProcessFilter`, String(this.buildingProcessId));
                this.$store.commit(`requests_setListPage`, 1);
            }
            this.$router.push({path: `/requests`}).catch(() => {});
        },
        openTasks() {
            this.$router.push({path: '/tasks', query: {building_process_id: this.buildingProcessId, tab: this.$store.getters.isAdmin ? 'all_employees' : 'mine'}}).catch(() => {});
        },
        openReports() {
            if (this.buildingProcessId) {
                this.$store.commit(`reports/setObjectStatus`, this.processForm.is_active ? `active` : `archive`);
                this.$store.commit(`reports/setBuildingProcessId`, String(this.buildingProcessId));
                this.$store.commit(`reports/setAuthorId`, `all`);
                this.$store.commit(`reports/setReportStatus`, this.relatedReportStatus);
                this.$store.commit(`reports/setPage`, 1);
            }
            this.$router.push({path: `/reports`}).catch(() => {});
        },
        openReport(report) {
            if (!report || !report.shift_id) {
                return;
            }
            this.$router.push(`/reports/${report.shift_id}`).catch(() => {});
        },
        async setRelatedReportStatus(value) {
            const next = [`active`, `annulled`].includes(value) ? value : `all`;
            if (this.relatedReportStatus === next) return;
            this.relatedReportStatus = next;
            await this.loadRelatedReports();
        },
        relatedReportRowClass(report) {
            return report && report.is_annulled ? `object-related-report--annulled` : ``;
        },
        numberText(value, digits = 2) {
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) {
                return `0`;
            }
            return parsed.toLocaleString(`ru-RU`, {
                minimumFractionDigits: 0,
                maximumFractionDigits: digits
            });
        },
        formatAmount(value, unit) {
            return `${this.numberText(value)} ${unit || ``}`.trim();
        },
        formatDateTime(value) {
            if (!value) {
                return ``;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return value;
            }
            return date.toLocaleString(`ru-RU`, {
                day: `2-digit`,
                month: `2-digit`,
                year: `numeric`,
                hour: `2-digit`,
                minute: `2-digit`
            });
        },
        daysSince(value) {
            if (!value) {
                return null;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return null;
            }
            const today = new Date();
            const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
            const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
            return Math.max(0, Math.floor((todayStart - dateStart) / 86400000));
        },
        pluralizeDays(value) {
            const number = Math.abs(Number(value) || 0);
            const mod10 = number % 10;
            const mod100 = number % 100;
            if (mod10 === 1 && mod100 !== 11) {
                return `день`;
            }
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
                return `дня`;
            }
            return `дней`;
        },
        lastReportBadge(card) {
            if (this.isCreate) {
                return null;
            }
            const latestShift = this.workProgress && this.workProgress.latest_shift
                ? this.workProgress.latest_shift
                : null;
            const date = (card && card.latest_report_date) || (latestShift && latestShift.date_time_begin);
            const days = this.daysSince(date);
            if (days === null) {
                return {
                    text: `нет отчётов`,
                    title: `По объекту ещё нет отчётов по сменам`,
                    variant: `secondary`
                };
            }
            const text = days === 0 ? `сегодня` : `${days} ${this.pluralizeDays(days)} назад`;
            return {
                text,
                title: `Последний отчёт: ${this.formatDateTime(date)}`,
                variant: days <= 1 ? `success` : (days <= 3 ? `info` : (days <= 7 ? `warning` : `danger`))
            };
        },
        splitConsumptionNotes(value) {
            return String(value || ``)
                .split(`-->`)
                .map(item => item.trim())
                .filter(Boolean);
        },
        workProgressPercent(work) {
            return Number(work && work.progress_percent) || 0;
        },
        roundProgressAmount(value) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
        },
        workProgressPercentFromAmounts(completed, plan) {
            const planValue = Number(plan) || 0;
            if (planValue <= 0) {
                return 0;
            }
            return Number((((Number(completed) || 0) / planValue) * 100).toFixed(1));
        },
        workProgressStatusFromAmounts(percent, completed, plan) {
            if ((Number(plan) || 0) <= 0) {
                return {status: `empty`, label: `Без объема`};
            }
            if ((Number(completed) || 0) <= 0) {
                return {status: `not_started`, label: `Не начато`};
            }
            if (percent >= 100) {
                return {
                    status: percent > 100 ? `overdone` : `completed`,
                    label: percent > 100 ? `Сверх договора` : `Завершено`
                };
            }
            return {status: `in_progress`, label: `В работе`};
        },
        isWorkOverrun(work) {
            return this.workProgressPercent(work) > 100;
        },
        hasWorkProgressIssue(work) {
            return this.isWorkOverrun(work) ||
                Number(work && work.material_issues_count) > 0 ||
                Number(work && work.work_overruns_count) > 0;
        },
        workProgressScaleMax(work) {
            const percent = this.workProgressPercent(work);
            if (percent <= 100) {
                return 100;
            }
            return Math.min(Math.max(percent, 120), 300);
        },
        workProgressContractMarkerPercent(work) {
            const scaleMax = this.workProgressScaleMax(work);
            return scaleMax > 0 ? (100 / scaleMax) * 100 : 100;
        },
        workProgressContractFillPercent(work) {
            const scaleMax = this.workProgressScaleMax(work);
            const percent = Math.min(this.workProgressPercent(work), 100);
            return scaleMax > 0 ? (percent / scaleMax) * 100 : 0;
        },
        workProgressOverrunFillPercent(work) {
            if (!this.isWorkOverrun(work)) {
                return 0;
            }
            const scaleMax = this.workProgressScaleMax(work);
            const overrunPercent = Math.min(this.workProgressPercent(work), scaleMax) - 100;
            return scaleMax > 0 ? (overrunPercent / scaleMax) * 100 : 0;
        },
        progressBarClass(percent) {
            const value = Number(percent) || 0;
            if (value >= 100) {
                return `object-progress-bar__fill object-progress-bar__fill--success`;
            }
            if (value > 0) {
                return `object-progress-bar__fill object-progress-bar__fill--primary`;
            }
            return `object-progress-bar__fill object-progress-bar__fill--muted`;
        },
        workProgressVariant(work) {
            const status = work && work.status;
            if (status === `completed`) {
                return `success`;
            }
            if (status === `overdone`) {
                return `danger`;
            }
            if (status === `in_progress`) {
                return `primary`;
            }
            if (status === `empty`) {
                return `warning`;
            }
            return `secondary`;
        },
        materialStatusVariant(material) {
            const status = material && material.status;
            if (status === `over_limit`) {
                return `danger`;
            }
            if (status === `under_limit`) {
                return `info`;
            }
            return `success`;
        },
        materialBarClass(material) {
            const status = material && material.status;
            if (status === `over_limit`) {
                return `object-progress-bar__fill object-progress-bar__fill--danger`;
            }
            if (status === `under_limit`) {
                return `object-progress-bar__fill object-progress-bar__fill--info`;
            }
            return `object-progress-bar__fill object-progress-bar__fill--success`;
        },
        materialScaleLimit(material) {
            const limit = Number(material && material.limit_percent);
            if (!Number.isFinite(limit) || limit < 0) {
                return 0;
            }
            return Math.min(limit, 100);
        },
        materialScalePosition(material) {
            const ratio = Number(material && material.ratio_percent);
            if (!Number.isFinite(ratio)) {
                return 50;
            }
            return Math.max(0, Math.min(ratio, 200)) / 2;
        },
        materialNormLeftPosition(material) {
            return Math.max(0, (100 - this.materialScaleLimit(material)) / 2);
        },
        materialNormRightPosition(material) {
            return Math.min(100, (100 + this.materialScaleLimit(material)) / 2);
        },
        materialNormBandStyle(material) {
            const left = this.materialNormLeftPosition(material);
            const right = this.materialNormRightPosition(material);
            return {
                left: `${left}%`,
                width: `${Math.max(1, right - left)}%`
            };
        },
        materialScaleStatus(material) {
            const ratio = Number(material && material.ratio_percent);
            if (!Number.isFinite(ratio)) {
                return `normal`;
            }
            const limit = this.materialScaleLimit(material);
            if (ratio > 100 + limit) {
                return `over_limit`;
            }
            if (ratio < 100 - limit) {
                return `under_limit`;
            }
            return `normal`;
        },
        materialScaleFillClass(material) {
            const status = this.materialScaleStatus(material);
            return [
                `object-material-scale__fill`,
                `object-material-scale__fill--${status}`
            ];
        },
        materialScaleMarkerClass(material) {
            const status = this.materialScaleStatus(material);
            return [
                `object-material-scale__marker`,
                `object-material-scale__marker--${status}`
            ];
        },
        materialScaleTitle(material) {
            const ratio = Number.isFinite(Number(material && material.ratio_percent))
                ? `${this.numberText(material.ratio_percent, 1)}% от нормы`
                : `По факту относительно нормы не рассчитано`;
            const limit = `допуск ±${this.numberText(this.materialScaleLimit(material), 2)}%`;
            return `${ratio}, ${limit}`;
        },
        setWorkProgressFilter(filter) {
            this.workProgressFilter = filter || `all`;
            this.workMaterialsExpanded = {};
            this.workGroupsExpanded = {};
        },
        setWorkProgressView(view) {
            this.workProgressView = view === `groups` ? `groups` : `works`;
            this.workMaterialsExpanded = {};
            this.workGroupsExpanded = {};
        },
        pluralizeWorks(count) {
            const value = Math.abs(Number(count) || 0);
            const mod10 = value % 10;
            const mod100 = value % 100;
            if (mod10 === 1 && mod100 !== 11) {
                return `работа`;
            }
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
                return `работы`;
            }
            return `работ`;
        },
        groupProgressTitle(group) {
            if (!group) {
                return ``;
            }
            const workNames = Array.isArray(group.work_names)
                ? group.work_names.filter(Boolean)
                : [];
            return [group.name].concat(workNames).join(` · `);
        },
        workProgressGroupKey(group) {
            return String(group && group.id || group && group.name || ``);
        },
        isWorkGroupExpanded(group) {
            return Boolean(this.workGroupsExpanded[this.workProgressGroupKey(group)]);
        },
        toggleWorkGroup(group) {
            const key = this.workProgressGroupKey(group);
            this.$set(this.workGroupsExpanded, key, !this.workGroupsExpanded[key]);
        },
        scrollProgressFiltersOnWheel(event) {
            const target = event && event.currentTarget;
            if (!target || event.ctrlKey || target.scrollWidth <= target.clientWidth) {
                return;
            }
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (!delta) {
                return;
            }
            const previousScrollLeft = target.scrollLeft;
            target.scrollLeft += delta;
            if (target.scrollLeft !== previousScrollLeft) event.preventDefault();
        },
        workProgressKey(work) {
            return String(work && (work.id || work.work_id || work.full_name));
        },
        isWorkMaterialsExpanded(work) {
            return Boolean(this.workMaterialsExpanded[this.workProgressKey(work)]);
        },
        workMaterialIssues(work) {
            return ((work && work.materials) || []).filter(material => material && material.limit_error);
        },
        workMaterialIssuesCount(work) {
            return this.workMaterialIssues(work).length;
        },
        visibleWorkMaterials(work) {
            if (this.isWorkMaterialsExpanded(work)) {
                return (work && work.materials) || [];
            }
            if (this.workProgressFilter === `material_issues`) {
                return this.workMaterialIssues(work);
            }
            return [];
        },
        hiddenWorkMaterialsCount(work) {
            const materials = (work && work.materials) || [];
            if (this.isWorkMaterialsExpanded(work)) {
                return 0;
            }
            if (this.workProgressFilter === `material_issues`) {
                return Math.max(0, materials.length - this.workMaterialIssues(work).length);
            }
            return materials.length;
        },
        hasHiddenWorkMaterials(work) {
            return this.hiddenWorkMaterialsCount(work) > 0;
        },
        signedPercent(value) {
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) {
                return null;
            }
            const sign = parsed > 0 ? `+` : ``;
            return `${sign}${this.numberText(parsed, 2)}%`;
        },
        materialRatioText(material) {
            if (!material) {
                return ``;
            }
            if (!Number.isFinite(Number(material.ratio_percent))) {
                return `По факту относительно нормы: нет данных`;
            }
            return `По факту: ${this.numberText(material.ratio_percent, 1)}% от нормы`;
        },
        materialDeviationText(material) {
            if (!material) {
                return ``;
            }
            const deviation = this.signedPercent(material.deviation_percent);
            if (deviation) {
                return `Отклонение: ${deviation}`;
            }
            return `Отклонение: нет данных`;
        },
        materialLimitText(material) {
            if (!material || !Number.isFinite(Number(material.limit_percent))) {
                return `Допуск: не задан`;
            }
            return `Допуск: ±${this.numberText(material.limit_percent, 2)}%`;
        },
        toggleWorkMaterials(work) {
            const key = this.workProgressKey(work);
            this.$set(this.workMaterialsExpanded, key, !this.workMaterialsExpanded[key]);
        },
        async downloadObjectReport() {
            if (!this.buildingProcessId) {
                return;
            }
            try {
                await this.$store.dispatch(`objectCards/downloadReport`, {
                    buildingProcessId: this.buildingProcessId,
                    chatId: this.processForm.common_chat_id,
                    objectName: this.title
                });
            } catch (err) {
                this.$bvToast.toast(this.errorText(err, `Не удалось скачать XLS отчет`), {
                    title: `Отчет объекта`,
                    variant: `danger`,
                    solid: true
                });
            }
        },
        errorText(err, fallback) {
            if (err && err.response && err.response.data) {
                return err.response.data.msg || err.response.data;
            }
            return fallback;
        }
    },
    mounted() {
        this.load().catch(err => {
            this.errorMessage = this.errorText(err, `Ошибка загрузки карточки объекта`);
        });
    }
};
</script>

<style scoped>
.object-card-page {
    padding: 8px;
    color: #212529;
}

.object-card-header {
    margin-bottom: 12px;
    padding: 12px 14px;
    border: 1px solid #d6dce3;
    border-left: 4px solid #6c757d;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
}

.object-card-header--active { border-left-color: #28a745; }
.object-card-header--without_process { border-left-color: #ffc107; }

.object-card-header__toolbar {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
}

.object-card-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    min-width: 0;
}

.object-card-title h1 {
    min-width: 0;
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.object-card-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
}

.object-card-actions > * { flex: 0 0 auto; }
.object-card-title .object-section-edit { flex-shrink: 0; margin-left: 0; }
.object-card-summary { margin-top: 8px; }
.object-card-context { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 16px; }
.object-card-address { display: flex; flex: 1 1 300px; align-items: baseline; gap: 6px; min-width: 0; color: #6c757d; font-size: 13px; line-height: 1.4; }
.object-card-address span { min-width: 0; overflow-wrap: anywhere; }
.object-card-details { max-width: 100%; margin-left: auto; color: #6c757d; font-size: 12px; }
.object-card-details summary { width: fit-content; padding: 3px 0; cursor: pointer; }
.object-card-details dl { display: flex; flex-wrap: wrap; gap: 6px 16px; margin: 6px 0 0; }
.object-card-details dt { font-weight: 400; }
.object-card-details dd { margin: 0; color: #495057; }
.object-summary-target { scroll-margin-top: 110px; }
.object-last-report { display: inline-flex; align-items: center; gap: 5px; padding: 3px 0; margin-right: 4px; color: #495057; font-size: 12px; }
a.object-last-report:hover { color: #0056b3; }
.object-health-chip:hover { text-decoration: underline; }
.object-health-chip:focus-visible, .object-last-report:focus-visible, .object-card-details summary:focus-visible, .object-summary-target:focus-visible { outline: 2px solid #80bdff; outline-offset: 2px; }

.object-card-content {
    min-width: 0;
}

.object-overview {
    margin-bottom: 12px;
}

.object-health-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-top: 6px;
}

.object-health-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 26px;
    padding: 3px 8px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
}

.object-health-chip--warning {
    border-color: #ffda6a;
    background: #fff3cd;
    color: #664d03;
}

.object-health-chip--danger {
    border-color: #f1aeb5;
    background: #f8d7da;
    color: #842029;
}

.object-overview-grid {
    display: grid;
    grid-template-columns: minmax(330px, .95fr) minmax(360px, 1.05fr);
    gap: 12px;
    align-items: start;
}

.object-process-panel {
    grid-column: 1 / -1;
}

.object-team-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 8px;
}

.object-person-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    min-width: 0;
    padding: 11px;
    border: 1px solid #e2e6ea;
    border-radius: 5px;
    background: #f8f9fa;
}

.object-person-card__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #084298;
    font-weight: 800;
}

.object-person-card__body {
    min-width: 0;
}

.object-person-card__body small {
    display: block;
    overflow: hidden;
    color: #6c757d;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-person-card__name {
    display: block;
    overflow: hidden;
    margin: 1px 0;
    color: #212529;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;
}

.object-person-card__name:hover {
    color: #0d6efd;
    text-decoration: none;
}

.object-person-card__contact {
    display: block;
    overflow: hidden;
    color: #0d6efd;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-access-list {
    margin-top: 12px;
}

.object-access-list__title {
    margin-bottom: 6px;
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.object-access-list__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.object-access-chip {
    max-width: 100%;
    padding: 4px 8px;
    border-radius: 999px;
    background: #e9ecef;
    color: #343a40;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
}

.object-access-chip:hover {
    background: #dbe4ff;
    color: #084298;
    text-decoration: none;
}

.object-fact-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.object-fact {
    min-width: 0;
    padding: 10px;
    border: 1px solid #e2e6ea;
    border-radius: 5px;
    background: #f8f9fa;
}

.object-fact--wide {
    grid-column: 1 / -1;
}

.object-fact__label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    color: #6c757d;
    font-size: 12px;
}

.object-fact__value {
    display: block;
    overflow: hidden;
    color: #212529;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-fact__value--button {
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    font-weight: 700;
    text-align: left;
}

.object-fact__value--button:hover {
    color: #0d6efd;
    text-decoration: underline;
}

.object-fact__value--multiline {
    overflow: visible;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.object-process-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}

.object-process-item {
    min-width: 0;
    padding: 10px;
    border: 1px solid #e2e6ea;
    border-radius: 5px;
    background: #f8f9fa;
}

.object-process-item strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-process-item span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    color: #6c757d;
    font-size: 12px;
}

.object-process-item strong {
    color: #212529;
}

.object-progress-panel {
    margin-top: 12px;
}

.object-progress-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.object-progress-actions .btn {
    min-width: 32px;
}

.object-progress-controls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 360px);
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
}

.object-progress-tabs {
    display: inline-flex;
    gap: 2px;
    margin-bottom: 10px;
    border-bottom: 1px solid #d7dee7;
}

.object-progress-tab {
    min-height: 32px;
    padding: 6px 12px;
    border: 1px solid transparent;
    border-bottom: 0;
    border-radius: 6px 6px 0 0;
    background: transparent;
    color: #495057;
    font-size: 13px;
    font-weight: 800;
}

.object-progress-tab--active {
    border-color: #d7dee7;
    background: #fff;
    color: #0d6efd;
    box-shadow: 0 -1px 0 #fff inset;
}

.object-progress-filters {
    display: flex;
    align-items: center;
    gap: 7px;
    overflow-x: auto;
    padding: 2px 0;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
}

.object-progress-filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    min-height: 30px;
    padding: 4px 8px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: #f8f9fa;
    color: #495057;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
    transition: box-shadow .15s ease, transform .15s ease, filter .15s ease;
}

.object-progress-filter:hover {
    filter: brightness(.97);
}

.object-progress-filter strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(255, 255, 255, .72);
    font-size: 11px;
    line-height: 1;
}

.object-progress-filter--active {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 0 0 2px rgba(0, 0, 0, .16);
}

.object-progress-filter--active strong {
    background: rgba(255, 255, 255, .92);
    color: #212529;
}

.object-progress-filter--primary {
    border-color: #b6d4fe;
    background: #e7f1ff;
    color: #084298;
}

.object-progress-filter--success {
    border-color: #a3cfbb;
    background: #d1e7dd;
    color: #0f5132;
}

.object-progress-filter--info {
    border-color: #9eeaf9;
    background: #cff4fc;
    color: #055160;
}

.object-progress-filter--danger {
    border-color: #f1aeb5;
    background: #f8d7da;
    color: #842029;
}

.object-progress-filter--primary.object-progress-filter--active {
    border-color: #0d6efd;
    background: #0d6efd;
}

.object-progress-filter--success.object-progress-filter--active {
    border-color: #28a745;
    background: #28a745;
}

.object-progress-filter--info.object-progress-filter--active {
    border-color: #17a2b8;
    background: #17a2b8;
}

.object-progress-filter--danger.object-progress-filter--active {
    border-color: #dc3545;
    background: #dc3545;
}

.object-progress-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 34px;
    border: 1px solid #b6c2cf;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .08);
}

.object-progress-search-wrap i {
    flex: 0 0 auto;
    padding-left: 12px;
    color: #6c757d;
}

.object-progress-search {
    height: 32px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    box-shadow: none;
    font-weight: 700;
}

.object-progress-search:focus {
    background: transparent;
    box-shadow: none;
}

.object-progress-search-wrap--active {
    border-color: #ffc107;
    box-shadow: 0 0 0 3px rgba(255, 193, 7, .24);
}

.object-progress-summary {
    display: flex;
    align-items: center;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #dce6f2;
    border-radius: 6px;
    background: #f8fbff;
}

.object-progress-summary__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.object-progress-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 27px;
    padding: 4px 8px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.15;
}

.object-progress-chip--primary {
    border-color: #b6d4fe;
    background: #e7f1ff;
    color: #084298;
}

.object-progress-chip--success {
    border-color: #a3cfbb;
    background: #d1e7dd;
    color: #0f5132;
}

.object-progress-chip--info {
    border-color: #9eeaf9;
    background: #cff4fc;
    color: #055160;
}

.object-progress-chip--danger {
    border-color: #f1aeb5;
    background: #f8d7da;
    color: #842029;
}

.object-progress-chip--secondary {
    border-color: #ced4da;
    background: #f1f3f5;
    color: #495057;
}

.object-progress-bar {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 9px;
    border-radius: 999px;
    background: #e9ecef;
}

.object-progress-bar--lg {
    height: 13px;
}

.object-progress-bar--sm {
    height: 7px;
}

.object-progress-bar__fill {
    display: block;
    height: 100%;
    min-width: 2px;
    border-radius: inherit;
    transition: width .2s ease;
}

.object-progress-bar--overrun .object-progress-bar__fill {
    position: absolute;
    top: 0;
    left: 0;
}

.object-progress-bar__fill--overrun {
    z-index: 2;
    border-radius: 0 999px 999px 0;
    background: #dc3545;
    transition: left .2s ease, width .2s ease;
}

.object-progress-bar__contract-mark {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 4;
    width: 2px;
    background: #212529;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, .9);
    transform: translateX(-1px);
}

.object-progress-bar__fill--primary {
    background: #0d6efd;
}

.object-progress-bar__fill--success {
    background: #28a745;
}

.object-progress-bar__fill--danger {
    background: #dc3545;
}

.object-progress-bar__fill--info {
    background: #17a2b8;
}

.object-progress-bar__fill--muted {
    background: #adb5bd;
}

.object-material-scale {
    position: relative;
    height: 34px;
    margin: 4px 0 7px;
}

.object-material-scale::before {
    content: "";
    position: absolute;
    right: 0;
    bottom: 4px;
    left: 0;
    height: 9px;
    border: 1px solid #dbe3ec;
    border-radius: 999px;
    background: linear-gradient(90deg, #eef9fc 0%, #f8fafc 50%, #fff2f2 100%);
}

.object-material-scale__label {
    position: absolute;
    top: 0;
    z-index: 5;
    color: #7b8491;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    pointer-events: none;
}

.object-material-scale__label--left {
    left: 0;
    color: #0c6374;
}

.object-material-scale__label--norm {
    left: 50%;
    color: #0f5132;
    transform: translateX(-50%);
}

.object-material-scale__label--right {
    right: 0;
    color: #842029;
}

.object-material-scale__norm {
    position: absolute;
    bottom: 2px;
    z-index: 1;
    height: 13px;
    border-radius: 999px;
    background: rgba(40, 167, 69, .18);
}

.object-material-scale__tick {
    position: absolute;
    bottom: 0;
    z-index: 4;
    width: 2px;
    height: 18px;
    border-radius: 2px;
    background: #343a40;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, .85);
    transform: translateX(-1px);
}

.object-material-scale__fill {
    position: absolute;
    bottom: 5px;
    left: 0;
    z-index: 2;
    height: 7px;
    min-width: 2px;
    border-radius: 999px;
    transition: width .2s ease;
}

.object-material-scale__fill--normal {
    background: #28a745;
}

.object-material-scale__fill--under_limit {
    background: #17a2b8;
}

.object-material-scale__fill--over_limit {
    background: #dc3545;
}

.object-material-scale__marker {
    position: absolute;
    bottom: 2px;
    z-index: 5;
    width: 12px;
    height: 12px;
    border: 2px solid #fff;
    border-radius: 999px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, .25);
    transform: translateX(-6px);
}

.object-material-scale__marker--normal {
    background: #28a745;
}

.object-material-scale__marker--under_limit {
    background: #17a2b8;
}

.object-material-scale__marker--over_limit {
    background: #dc3545;
}

.object-work-progress-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.object-work-progress-card {
    min-width: 0;
    padding: 11px;
    border: 1px solid #d7dee7;
    border-left: 4px solid #adb5bd;
    border-radius: 6px;
    background: #fff;
}

.object-work-progress-card--in_progress {
    border-left-color: #0d6efd;
}

.object-work-progress-card--completed {
    border-left-color: #28a745;
}

.object-work-progress-card--overdone {
    border-left-color: #dc3545;
}

.object-work-progress-card--empty,
.object-work-progress-card--not_started {
    border-left-color: #adb5bd;
    background: #fbfcfe;
}

.object-work-progress-card--disabled,
.object-work-group-work--disabled,
.object-work-material--disabled {
    filter: grayscale(.55);
    opacity: .62;
}

.object-work-progress-card--disabled:hover,
.object-work-group-work--disabled:hover,
.object-work-material--disabled:hover {
    opacity: .82;
}

.object-work-progress-card--group {
    background: #fcfdff;
}

.object-work-progress-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 9px;
}

.object-work-progress-card__header > div {
    min-width: 0;
}

.object-work-progress-card__side {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 6px;
    flex: 0 0 auto;
}

.object-work-progress-card__actions {
    display: flex;
    gap: 4px;
}

.object-work-progress-card__actions .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
}

.object-work-progress-card__actions .object-lifecycle-button,
.object-work-material__actions .object-lifecycle-button {
    height: 28px;
    padding: 0 7px;
    white-space: nowrap;
    width: auto !important;
}

.object-work-progress-card__title {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 2px 6px;
    min-width: 0;
    line-height: 1.25;
}

.object-work-progress-card__title strong {
    min-width: 0;
    color: #212529;
    font-size: 14px;
    overflow-wrap: anywhere;
}

.object-work-progress-card__title span {
    color: #6c757d;
    font-size: 12px;
    font-weight: 600;
    overflow-wrap: anywhere;
}

.object-work-progress-card__title span::before {
    content: "·";
    margin-right: 6px;
    color: #adb5bd;
}

.object-work-progress-card__group {
    color: #8a94a3 !important;
}

.object-work-progress-card__meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
}

.object-work-progress-card__meta span:last-child {
    min-width: 0;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-work-progress-card__details {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 10px;
    margin-top: 7px;
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
}

.object-work-group-works {
    display: grid;
    gap: 7px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5ebf2;
}

.object-work-group-work {
    min-width: 0;
    padding: 8px;
    border: 1px solid #e1e8f0;
    border-left: 3px solid #adb5bd;
    border-radius: 5px;
    background: #fff;
}

.object-work-group-work--in_progress {
    border-left-color: #0d6efd;
}

.object-work-group-work--completed {
    border-left-color: #28a745;
}

.object-work-group-work--overdone {
    border-left-color: #dc3545;
}

.object-work-group-work__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.object-work-group-work__title {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 2px 6px;
    min-width: 0;
}

.object-work-group-work__title strong {
    min-width: 0;
    color: #212529;
    font-size: 13px;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.object-work-group-work__title span {
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.object-work-group-work__title span::before {
    content: "·";
    margin-right: 6px;
    color: #adb5bd;
}

.object-work-group-work__meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 5px;
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
}

.object-work-group-work__meta span:last-child {
    min-width: 0;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-work-materials-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 28px;
    padding: 0;
    margin-top: 9px;
    border: 0;
    background: transparent;
    color: #0d6efd;
    font-size: 12px;
    font-weight: 800;
}

.object-work-materials-toggle--nested {
    margin-top: 7px;
}

.object-work-materials-toggle__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #e7f1ff;
    color: #084298;
    font-size: 11px;
}

.object-work-materials-toggle__count--danger {
    background: #f8d7da;
    color: #842029;
}

.object-work-materials {
    display: grid;
    gap: 7px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5eaf1;
}

.object-work-materials--issues {
    border-top-color: #f1aeb5;
}

.object-work-materials--nested {
    padding-top: 7px;
}

.object-work-materials__title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #6c757d;
    font-size: 12px;
    font-weight: 800;
}

.object-work-materials--issues .object-work-materials__title {
    color: #842029;
}

.object-work-material {
    padding: 8px;
    border: 1px solid #e1e8f0;
    border-radius: 5px;
    background: #f8f9fa;
}

.object-work-material--over_limit {
    border-color: #f1aeb5;
    background: #fff5f5;
}

.object-work-material--under_limit {
    border-color: #9eeaf9;
    background: #f2fcff;
}

.object-work-material__header,
.object-work-material__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.object-work-material__header {
    margin-bottom: 7px;
}

.object-work-material__side {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 5px;
    margin-left: auto;
}

.object-work-material__actions {
    display: flex;
    gap: 4px;
}

.object-work-material__actions .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
}

.object-work-material__header strong {
    min-width: 0;
    overflow: hidden;
    color: #212529;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-work-material__meta {
    margin-top: 5px;
    color: #6c757d;
    font-size: 12px;
}

.object-work-material__meta span {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 7px;
    border: 1px solid #dbe3ec;
    border-radius: 999px;
    background: #fff;
    white-space: normal;
}

.object-work-material__meta-danger {
    border-color: #f1aeb5 !important;
    background: #f8d7da !important;
    color: #842029;
    font-weight: 800;
}

.object-empty-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px;
    border: 1px dashed #ced4da;
    border-radius: 5px;
    background: #f8f9fa;
    color: #6c757d;
    font-weight: 700;
}

.object-card-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(360px, 0.75fr);
    gap: 12px;
    align-items: start;
}

.object-card-main,
.object-card-side {
    min-width: 0;
}

.object-panel {
    border: 1px solid #d6dce3;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
}

.object-panel__header {
    margin-bottom: 10px;
}

.object-panel__header h6 {
    margin: 0;
    font-weight: 700;
}

.object-panel__header--split {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.object-panel__header-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
}

.object-section-edit {
    min-width: 28px;
    height: 28px;
    padding: 0;
}

.object-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
}

.object-form-grid__wide {
    grid-column: 1 / -1;
}

.object-readonly-box {
    padding: 8px 10px;
    border: 1px solid #d6dce3;
    border-radius: 4px;
    background: #f8f9fa;
}

.object-card-tabs {
    margin-top: 12px;
}

.object-mobile-tab-grid {
    display: none;
}

.object-card-tabs >>> .nav-tabs {
    border-bottom-color: #d6dce3;
}

.object-card-tabs >>> .nav-link {
    border-radius: 4px 4px 0 0;
    color: #495057;
    font-weight: 700;
}

.object-card-tabs >>> .nav-link.active {
    color: #0d6efd;
}

.object-card-tabs >>> .nav-link {
    white-space: nowrap;
}

.object-card-tabs >>> .tab-content {
    padding: 12px;
    border: 1px solid #d6dce3;
    border-top: 0;
    border-radius: 0 0 4px 4px;
    background: #fff;
}

.object-tab-placeholder {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    min-height: 84px;
    padding: 14px;
    border: 1px dashed #ced4da;
    border-radius: 5px;
    background: #f8f9fa;
}

.object-tab-placeholder > div {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 2px 8px;
    align-items: center;
    min-width: 0;
}

.object-tab-placeholder i {
    grid-row: span 2;
    color: #6c757d;
    font-size: 18px;
}

.object-tab-placeholder strong,
.object-tab-placeholder span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-tab-placeholder span {
    color: #6c757d;
    font-size: 13px;
}

.object-tab-placeholder--muted {
    color: #6c757d;
}

.object-related-list {
    min-width: 0;
}

.object-related-list__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.object-related-list__header h6 {
    margin: 0;
    font-weight: 800;
}

.object-related-list >>> .request-short-card,
.object-related-list >>> .card {
    margin-top: 8px !important;
}

.object-mobile-data-list {
    display: none;
}

.object-desktop-data-table {
    min-width: 0;
}

.object-consumption-table {
    font-size: 12px;
}

.object-consumption-table >>> th {
    white-space: nowrap;
}

.object-consumption-table >>> td {
    vertical-align: top;
}

.object-related-reports-table {
    cursor: pointer;
    font-size: 12px;
}

.object-related-reports-table >>> th {
    white-space: nowrap;
}

.object-related-reports-table >>> td {
    vertical-align: top;
}

.object-related-reports-table >>> tr.object-related-report--annulled > td {
    background: #fff3f3;
    color: #721c24;
}

.object-consumption-notes {
    margin: 0;
    padding-left: 16px;
}

.target-form-select--valid >>> .vs__dropdown-toggle {
    border-color: #28a745;
}

.target-form-select--invalid >>> .vs__dropdown-toggle {
    border-color: #dc3545;
}

.object-material-target-context {
    display: grid;
    gap: 8px;
    margin-bottom: 14px;
    padding: 11px 13px;
    border: 1px solid #e2e7ed;
    border-radius: 9px;
    background: #f8f9fa;
}

.object-material-target-context__row {
    display: grid;
    align-items: baseline;
    grid-template-columns: 62px minmax(0, 1fr);
    gap: 10px;
}

.object-material-target-context__row span,
.object-material-target-editor__heading span {
    color: #6c757d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.object-material-target-context__row strong {
    min-width: 0;
    line-height: 1.35;
}

.object-material-target-editor {
    margin-bottom: 14px;
    padding: 13px;
    border: 1px solid #d7e1ed;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(31, 52, 78, 0.06);
}

.object-material-target-editor__heading {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.object-material-target-editor__heading > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.object-material-target-editor__heading strong {
    overflow: hidden;
    color: #212529;
    font-size: 1rem;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.object-material-target-editor__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 9px;
    color: #1767cf !important;
    background: #eaf2ff;
    font-size: 0.9rem !important;
}

.object-material-target-scope {
    display: flex;
    align-items: flex-start;
    margin-top: 8px;
    padding: 8px 10px;
    border: 1px solid #cfe0fa;
    border-radius: 9px;
    color: #24466f;
    background: #f2f7ff;
}

.object-material-target-scope > i {
    flex: 0 0 auto;
    margin-top: 3px;
    margin-right: 8px;
    color: #1767cf;
}

.object-material-target-scope div {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.object-material-target-scope span {
    color: #63748a;
    font-size: 0.78rem;
}

.object-material-target-scope strong {
    margin-top: 2px;
    line-height: 1.3;
}

@media (max-width: 575px) {
    .object-material-target-context__row {
        grid-template-columns: 1fr;
        gap: 1px;
    }

    .object-material-target-editor {
        padding: 11px;
    }
}

@media (max-width: 1100px) {
    .object-card-layout {
        grid-template-columns: 1fr;
    }

    .object-overview-grid,
    .object-work-progress-grid {
        grid-template-columns: 1fr;
    }

    .object-process-panel {
        grid-column: auto;
    }

    .object-progress-controls {
        grid-template-columns: 1fr;
    }

    .object-process-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 767.98px) {
    .object-card-page {
        padding: 6px;
    }

    .object-card-header {
        margin-bottom: 8px;
        padding: 10px;
    }

    .object-card-header__toolbar {
        grid-template-columns: auto minmax(0, 1fr);
        gap: 8px;
    }

    .object-card-title {
        grid-row: 2;
        grid-column: 1 / -1;
    }

    .object-card-title h1 {
        font-size: 17px;
    }

    .object-card-actions {
        grid-row: 1;
        grid-column: 2;
        gap: 6px;
    }

    .object-card-details {
        margin-left: 0;
    }

    .object-health-row {
        gap: 5px;
    }

    .object-health-chip {
        max-width: 100%;
        min-height: 32px;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .object-team-grid {
        grid-template-columns: 1fr;
    }

    .object-progress-tabs {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6px;
        margin-bottom: 8px;
        border-bottom: 0;
    }

    .object-progress-tab {
        border: 1px solid #d7dee7;
        border-radius: 6px;
        background: #f8f9fa;
    }

    .object-progress-tab--active {
        background: #fff;
        box-shadow: none;
    }

    .object-progress-controls {
        gap: 8px;
        margin-bottom: 10px;
    }

    .object-progress-filters {
        margin-right: -6px;
        padding: 2px 6px 6px 0;
    }

    .object-progress-filter {
        min-height: 32px;
    }

    .object-progress-search-wrap {
        border-radius: 8px;
    }

    .object-progress-search {
        border-radius: 8px;
    }

    .object-progress-summary {
        padding: 10px;
    }

    .object-work-progress-grid {
        gap: 8px;
    }

    .object-work-progress-card {
        padding: 10px;
        border-radius: 5px;
    }

    .object-work-progress-card__title strong,
    .object-work-progress-card__title span,
    .object-work-group-work__title strong,
    .object-work-group-work__title span {
        overflow-wrap: anywhere;
        word-break: normal;
    }

    .object-card-tabs {
        margin-top: 0;
    }

    .object-card-tabs >>> .nav-tabs {
        display: none;
    }

    .object-mobile-tab-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        margin-top: 8px;
        margin-bottom: 8px;
    }

    .object-mobile-tab-card {
        align-items: center;
        background: #fff;
        border: 1px solid #d7dee7;
        border-radius: 9px;
        color: #495057;
        display: grid;
        gap: 8px;
        grid-template-columns: 24px minmax(0, 1fr) auto;
        min-height: 62px;
        padding: 9px 10px;
        text-align: left;
        transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
        width: 100%;
    }

    .object-mobile-tab-card > i {
        color: #6c757d;
        font-size: 1rem;
        text-align: center;
    }

    .object-mobile-tab-card__label {
        font-size: 0.86rem;
        font-weight: 700;
        min-width: 0;
        overflow-wrap: anywhere;
    }

    .object-mobile-tab-card__status {
        align-items: flex-end;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 24px;
    }

    .object-mobile-tab-card__status strong {
        color: #343a40;
        font-size: 0.95rem;
        line-height: 1;
    }

    .object-mobile-tab-card__status small {
        color: #dc3545;
        font-size: 0.66rem;
        font-weight: 700;
        line-height: 1.1;
        margin-top: 4px;
        white-space: nowrap;
    }

    .object-mobile-tab-card--active {
        background: #eef6ff;
        border-color: #0d6efd;
        box-shadow: 0 0 0 1px rgba(13, 110, 253, 0.12);
        color: #0b5ed7;
    }

    .object-mobile-tab-card--active > i,
    .object-mobile-tab-card--active .object-mobile-tab-card__status strong {
        color: #0d6efd;
    }

    .object-mobile-tab-card--error {
        border-color: #dc3545;
    }

    .object-mobile-tab-card--error .object-mobile-tab-card__status > i {
        color: #dc3545;
    }

    .object-mobile-tab-card:focus {
        box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
        outline: none;
    }

    .object-card-tabs >>> .tab-content {
        border-radius: 4px;
        border-top: 1px solid #d6dce3;
        padding: 8px;
    }

    .object-desktop-data-table {
        display: none;
    }

    .object-mobile-data-list {
        display: grid;
        gap: 8px;
        min-width: 0;
    }

    .object-mobile-data-card {
        background: #fff;
        border: 1px solid #dfe4ea;
        border-radius: 8px;
        color: #212529;
        min-width: 0;
        overflow: hidden;
        overflow-wrap: anywhere;
        padding: 10px;
        text-align: left;
        width: 100%;
    }

    .object-mobile-data-card__header {
        align-items: flex-start;
        display: flex;
        gap: 8px;
        justify-content: space-between;
        min-width: 0;
    }

    .object-mobile-data-card__header > div:first-child {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .object-mobile-data-card__header strong,
    .object-mobile-data-card__section strong,
    .object-mobile-data-card__section span,
    .object-mobile-data-card__meta strong,
    .object-mobile-data-card__meta a {
        overflow-wrap: anywhere;
        word-break: normal;
    }

    .object-mobile-data-card__header > .badge {
        flex: 0 0 auto;
        max-width: 45%;
        white-space: normal;
    }

    .object-mobile-data-card small {
        color: #6c757d;
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        line-height: 1.2;
        margin-bottom: 2px;
    }

    .object-mobile-data-card__section {
        background: #f8f9fa;
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        margin-top: 8px;
        min-width: 0;
        padding: 8px;
    }

    .object-mobile-data-card__section span {
        color: #6c757d;
        font-size: 0.78rem;
        margin-top: 2px;
    }

    .object-mobile-data-card__meta {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin-top: 9px;
        min-width: 0;
    }

    .object-mobile-data-card__meta > span {
        min-width: 0;
    }

    .object-mobile-data-card__meta strong,
    .object-mobile-data-card__meta a {
        display: block;
        font-size: 0.78rem;
        line-height: 1.3;
    }

    .object-mobile-data-card__details {
        border-top: 1px solid #edf0f3;
        margin-top: 9px;
        padding-top: 8px;
    }

    .object-mobile-report-card {
        cursor: pointer;
    }

    .object-mobile-report-card:focus {
        border-color: #0d6efd;
        box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.18);
        outline: none;
    }

    .object-mobile-report-card--annulled {
        background: #fff5f5;
        border-color: #dc3545;
    }

    .object-mobile-report-card__badges {
        align-items: flex-end;
        display: flex;
        flex: 0 0 auto;
        flex-direction: column;
        gap: 4px;
        max-width: 48%;
    }

    .object-mobile-report-card__badges .badge {
        max-width: 100%;
        white-space: normal;
    }

    .object-mobile-report-card__indicators {
        border-top: 1px solid #edf0f3;
        margin-top: 9px;
        min-width: 0;
        padding-top: 8px;
    }

    .object-report-status-filter {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
    }

    .object-report-status-filter .btn {
        border-radius: 0;
        min-width: 0;
        padding-left: 4px;
        padding-right: 4px;
        white-space: normal;
    }

    .object-report-status-filter .btn:first-child {
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;
    }

    .object-report-status-filter .btn:last-child {
        border-bottom-right-radius: 4px;
        border-top-right-radius: 4px;
    }
}

@media (max-width: 576px) {
    .object-section-edit {
        margin-left: auto;
    }

    .object-fact-grid,
    .object-process-summary {
        grid-template-columns: 1fr;
    }

    .object-tab-placeholder {
        align-items: stretch;
        flex-direction: column;
    }

    .object-related-list__header {
        align-items: stretch;
        flex-direction: column;
    }

    .object-tab-placeholder .btn {
        width: 100%;
    }

    .object-form-grid {
        grid-template-columns: 1fr;
    }

    .object-panel__header--split {
        align-items: flex-start;
        flex-direction: column;
    }

    .object-progress-actions,
    .object-progress-actions .btn,
    .object-work-materials-toggle {
        width: 100%;
    }

    .object-progress-actions .btn,
    .object-work-materials-toggle {
        justify-content: center;
    }

    .object-work-progress-card__meta {
        align-items: flex-start;
        flex-direction: column;
        gap: 2px;
    }

    .object-work-progress-card__header,
    .object-work-group-work__header,
    .object-work-material__header {
        align-items: flex-start;
        flex-direction: column;
    }

    .object-work-progress-card__side,
    .object-work-material__side {
        align-items: flex-start;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        margin-left: 0;
    }

    .object-work-progress-card__actions,
    .object-work-material__actions {
        margin-left: auto;
    }

    .object-work-material__header strong {
        overflow: visible;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .object-work-group-work__meta {
        align-items: flex-start;
        flex-direction: column;
        gap: 2px;
    }

    .object-work-group-work__meta span:last-child {
        text-align: left;
        white-space: normal;
    }

    .object-work-material__meta {
        align-items: flex-start;
        gap: 5px;
    }

    .object-work-material__meta span {
        white-space: normal;
    }

    .object-work-progress-card__meta span:last-child {
        text-align: left;
        white-space: normal;
    }
}
</style>
